const STATE_KEY = "hydrotools:point-cloud-workspace:v1";
const ENABLE_ABSOLUTE_Z_CLIP = true;

function esc(value) {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

const ELEVATION_RAMPS = ["VIRIDIS", "INFERNO", "PLASMA", "SPECTRAL", "RAINBOW"];
const INTENSITY_RAMPS = ["GRAYSCALE", "COPPER", "BLUE_ORANGE", "ICE_FIRE"];

function rampsForAttribute(attribute) {
  const a = String(attribute || "").toLowerCase();
  return a === "intensity" ? INTENSITY_RAMPS : ELEVATION_RAMPS;
}

function readState() {
  const fallback = {
    filePath: "",
    maxPreviewPoints: 12000,
    potreeDatasetName: "",
    rasterResolution: 1,
    rasterColorRamp: "terrain",
    rasterStretchMin: 0,
    rasterStretchMax: 100,
    rasterOpacity: 0.92,
    potreePointBudget: 3000000,
    potreePointSize: 0.8,
    potreeVerticalExaggeration: 1,
    potreePointShape: "CIRCLE",
    potreeAxisEnabled: true,
    potreeAxisTicksEnabled: true,
    potreeEdlEnabled: false,
    potreeEdlRadius: 1.0,
    potreeEdlStrength: 0.3,
    potreeEdlOpacity: 1.0,
    potreeSurfaceEnabled: false,
    potreeSurfaceOpacity: 0.7,
    potreeAttribute: "classification",
    potreeGradient: "VIRIDIS",
    potreeElevationMin: 0,
    potreeElevationMax: 100,
    potreeBoundsManualEnabled: false,
    potreeBoundsManualMin: 0,
    potreeBoundsManualMax: 100,
    potreeZClipEnabled: false,
    potreeZClipMin: 0,
    potreeZClipMax: 100,
    potreeClassVisibility: {},
    potreeClassColors: {},
    potreeLastJobId: "",
    potreeProjects: [],
    epsgCode: "",
    tileZoom: 18,
    hiresResolution: 0.1,
    hiresMinZoom: 14,
    hiresMaxZoom: 20,
    projectFiles: [],
  };
  try {
    const raw = localStorage.getItem(STATE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return {
      ...fallback,
      ...(parsed && typeof parsed === "object" ? parsed : {}),
      maxPreviewPoints: Math.max(1000, Math.min(50000, Number(parsed?.maxPreviewPoints || fallback.maxPreviewPoints) || fallback.maxPreviewPoints)),
      rasterResolution: Math.max(0.1, Math.min(10, Number(parsed?.rasterResolution || fallback.rasterResolution) || fallback.rasterResolution)),
      rasterColorRamp: String(parsed?.rasterColorRamp || fallback.rasterColorRamp || "terrain"),
      rasterStretchMin: Number(parsed?.rasterStretchMin ?? fallback.rasterStretchMin),
      rasterStretchMax: Number(parsed?.rasterStretchMax ?? fallback.rasterStretchMax),
      rasterOpacity: Math.max(0, Math.min(1, Number(parsed?.rasterOpacity ?? fallback.rasterOpacity) || fallback.rasterOpacity)),
      potreePointBudget: Math.max(100000, Math.min(30000000, Number(parsed?.potreePointBudget || fallback.potreePointBudget) || fallback.potreePointBudget)),
      potreePointSize: Math.max(0.1, Math.min(10, Number(parsed?.potreePointSize || fallback.potreePointSize) || fallback.potreePointSize)),
      potreeVerticalExaggeration: Math.max(1, Math.min(10, Number(parsed?.potreeVerticalExaggeration ?? fallback.potreeVerticalExaggeration) || fallback.potreeVerticalExaggeration)),
      potreePointShape: String(parsed?.potreePointShape || fallback.potreePointShape || "CIRCLE").toUpperCase(),
      potreeAxisEnabled: parsed?.potreeAxisEnabled == null ? true : !!parsed?.potreeAxisEnabled,
      potreeAxisTicksEnabled: parsed?.potreeAxisTicksEnabled == null ? true : !!parsed?.potreeAxisTicksEnabled,
      potreeEdlEnabled: !!parsed?.potreeEdlEnabled,
      potreeEdlRadius: Math.max(0.1, Math.min(4, Number(parsed?.potreeEdlRadius ?? fallback.potreeEdlRadius) || fallback.potreeEdlRadius)),
      potreeEdlStrength: Math.max(0, Math.min(5, Number(parsed?.potreeEdlStrength ?? fallback.potreeEdlStrength) || fallback.potreeEdlStrength)),
      potreeEdlOpacity: Math.max(0, Math.min(1, Number(parsed?.potreeEdlOpacity ?? fallback.potreeEdlOpacity) || fallback.potreeEdlOpacity)),
      potreeSurfaceEnabled: !!parsed?.potreeSurfaceEnabled,
      potreeSurfaceOpacity: Math.max(0.1, Math.min(1, Number(parsed?.potreeSurfaceOpacity ?? fallback.potreeSurfaceOpacity) || fallback.potreeSurfaceOpacity)),
      potreeAttribute: String(parsed?.potreeAttribute || fallback.potreeAttribute || "classification"),
      potreeGradient: String(parsed?.potreeGradient || fallback.potreeGradient || "VIRIDIS").toUpperCase(),
      potreeElevationMin: Number(parsed?.potreeElevationMin ?? fallback.potreeElevationMin),
      potreeElevationMax: Number(parsed?.potreeElevationMax ?? fallback.potreeElevationMax),
      potreeBoundsManualEnabled: !!parsed?.potreeBoundsManualEnabled,
      potreeBoundsManualMin: Number(parsed?.potreeBoundsManualMin ?? fallback.potreeBoundsManualMin),
      potreeBoundsManualMax: Number(parsed?.potreeBoundsManualMax ?? fallback.potreeBoundsManualMax),
      potreeZClipEnabled: !!parsed?.potreeZClipEnabled,
      potreeZClipMin: Number(parsed?.potreeZClipMin ?? fallback.potreeZClipMin),
      potreeZClipMax: Number(parsed?.potreeZClipMax ?? fallback.potreeZClipMax),
      potreeClassVisibility: {
        ...fallback.potreeClassVisibility,
        ...(parsed?.potreeClassVisibility && typeof parsed.potreeClassVisibility === "object" ? parsed.potreeClassVisibility : {}),
      },
      potreeClassColors: {
        ...fallback.potreeClassColors,
        ...(parsed?.potreeClassColors && typeof parsed.potreeClassColors === "object" ? parsed.potreeClassColors : {}),
      },
      potreeLastJobId: String(parsed?.potreeLastJobId || fallback.potreeLastJobId || ""),
      potreeProjects: [],
      epsgCode: String(parsed?.epsgCode || fallback.epsgCode || ""),
      tileZoom: Math.max(12, Math.min(21, Math.round(Number(parsed?.tileZoom || fallback.tileZoom) || 18))),
      hiresResolution: Math.max(0.01, Math.min(10, Number(parsed?.hiresResolution || fallback.hiresResolution) || fallback.hiresResolution)),
      hiresMinZoom: Math.max(8, Math.min(20, Math.round(Number(parsed?.hiresMinZoom || fallback.hiresMinZoom) || fallback.hiresMinZoom))),
      hiresMaxZoom: Math.max(14, Math.min(24, Math.round(Number(parsed?.hiresMaxZoom || fallback.hiresMaxZoom) || fallback.hiresMaxZoom))),
      projectFiles: Array.isArray(parsed?.projectFiles)
        ? parsed.projectFiles
          .map((item) => ({
            id: String(item?.id || `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`),
            name: String(item?.name || item?.sourcePath || "Point Cloud"),
            sourcePath: String(item?.sourcePath || ""),
            potreeJobId: String(item?.potreeJobId || ""),
            status: String(item?.status || "ready"),
            selected: item?.selected !== false,
            crsInspection: item?.crsInspection && typeof item.crsInspection === "object" ? item.crsInspection : null,
          }))
          .filter((item) => item.sourcePath)
        : [],
    };
  } catch {
    return fallback;
  }
}

function writeState(state) {
  try { localStorage.setItem(STATE_KEY, JSON.stringify(state)); } catch {}
}

async function requestJson(url, options = {}) {
  const response = await fetch(url, {
    method: options.method || "GET",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    body: options.body == null ? undefined : JSON.stringify(options.body),
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(String(payload?.error || `Request failed (${response.status})`));
  return payload;
}

const THREE_LIBS = {
  cache: null,
  async load() {
    if (!this.cache) {
      this.cache = (async () => {
        const THREE = await import("three");
        let controls = null;
        try {
          controls = await import("three/examples/jsm/controls/OrbitControls.js");
        } catch {
          controls = await import("three/addons/controls/OrbitControls.js");
        }
        return {
          THREE,
          OrbitControls: controls.OrbitControls,
        };
      })();
    }
    return this.cache;
  },
};

export const pointCloudWorkspaceTool = {
  id: "point-cloud-workspace",
  name: "Point Cloud Workspace",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, el) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(el);
  },
  setPanelOpen(_app, open) { this._rt?.setPanelOpen?.(open); },
  serialize() { return this._rt?.snapshot?.() ?? null; },
  restore(app, data) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.restore?.(data);
  },
};

export function createPointCloudWorkspaceRuntime(app) {
  return createRuntime(app);
}

function createRuntime(app) {
  const Z_NUDGE_STEP = 0.1;
  const TASK_SOURCE = "point-cloud-workspace";
  function taskStatus(message, progress = null, running = true) {
    app?.events?.emit?.("task:status", {
      source: TASK_SOURCE,
      label: "Point Cloud",
      message,
      progress,
      status: running ? "running" : "idle",
    });
  }
  function taskIdle() { taskStatus("", null, false); }

  let containerEl = null;
  let busy = false;
  let state = readState();
  let status = "";
  let dataset = null;
  let potreeBusy = false;
  let tiffExportBusy = false;
  let tiffExportProgress = 0;
  let tiffExportMessage = "";
  let tiffExportPollTimer = null;
  let tileExportJob = null;
  let tileExportKind = "";
  let lastTileJobId = null;
  let lastHiresJobId = null;
  let potreeJob = null;
  let depthLayerAvailable = false;
  let surfaceStatus = "";
  let surfaceStatusMsg = "";
  let potreePollTimer = null;
  let projectJobsPollTimer = null;
  let potreeViewerWindow = null;
  let projectAutoOpenFirstCompleted = false;
  let lastRangeBoundsKey = "";
  let potreeBoundsHydratePromise = null;
  let preview = {
    destroy: null,
    setPaused: null,
  };
  let panelOpen = true;
  const makeProjectItemId = () => `pcf-${Date.now()}-${Math.random().toString(16).slice(2, 8)}`;
  let activeMarinaCode = "";
  let savePotreeProjectsTimer = null;
  let lasCrsInspectRun = 0;

  function isProjectS3Path(path = "") {
    const p = String(path || "").trim();
    return p.startsWith("point-clouds/")
      || p.startsWith("marinas/")
      || p.startsWith("clients/")
      || p.startsWith("projects/");
  }

  // Auto-add S3 filePath to projectFiles if not already listed (removes need for manual "Add S3 Path")
  function autoAddFilePathToProject() {
    const fp = String(state.filePath || "").trim();
    if (!fp || !isProjectS3Path(fp)) return;
    const already = (state.projectFiles || []).some(
      (item) => String(item?.sourcePath || "").trim().toLowerCase() === fp.toLowerCase()
    );
    if (!already) {
      state.projectFiles = [...(state.projectFiles || []), {
        id: makeProjectItemId(),
        name: fp.split("/").pop() || "Point Cloud",
        sourcePath: fp,
        potreeJobId: "", status: "ready", progressPct: 0, viewerUrl: "",
      }];
      writeState(state);
    }
  }
  autoAddFilePathToProject();


  function persist() {
    state.potreeLastJobId = String(potreeJob?.id || state.potreeLastJobId || "");
    const { potreeProjects: _omit, ...stateToSave } = state;
    writeState(stateToSave);
  }

  function normalizePotreeProject(p) {
    return {
      id: String(p?.id || makeProjectItemId()),
      name: String(p?.name || "Point Cloud"),
      jobId: String(p?.jobId || ""),
      status: String(p?.status || "queued"),
      progressPct: Number(p?.progressPct || 0),
      viewerUrl: String(p?.viewerUrl || ""),
      message: String(p?.message || ""),
      createdAt: Number(p?.createdAt || 0),
      sourcePaths: Array.isArray(p?.sourcePaths) ? p.sourcePaths.map(String) : [],
    };
  }

  async function loadPotreeProjects(code) {
    if (!code) return;
    try {
      const data = await requestJson(`/api/point-cloud/potree/projects?marina=${encodeURIComponent(code)}`);
      if (code !== activeMarinaCode) return;
      let projects = Array.isArray(data.projects) ? data.projects.map(normalizePotreeProject) : [];

      // One-time migration: if S3 has no projects, check if localStorage still has them from before S3 persistence
      if (projects.length === 0) {
        try {
          const raw = localStorage.getItem(STATE_KEY);
          const parsed = raw ? JSON.parse(raw) : {};
          const localProjects = Array.isArray(parsed?.potreeProjects) ? parsed.potreeProjects : [];
          if (localProjects.length > 0) {
            projects = localProjects.map(normalizePotreeProject);
            requestJson("/api/point-cloud/potree/projects", {
              method: "POST",
              body: { marina: code, projects },
            }).catch(() => {});
            // Remove from localStorage so migration only runs once
            try {
              const { potreeProjects: _drop, ...rest } = parsed;
              localStorage.setItem(STATE_KEY, JSON.stringify(rest));
            } catch {}
          }
        } catch {}
      }

      state.potreeProjects = projects;
      render();
    } catch (err) {
      console.warn("[pcw] Failed to load potree projects:", err);
    }
  }

  function savePotreeProjects() {
    if (!activeMarinaCode) return;
    clearTimeout(savePotreeProjectsTimer);
    savePotreeProjectsTimer = setTimeout(async () => {
      try {
        await requestJson("/api/point-cloud/potree/projects", {
          method: "POST",
          body: { marina: activeMarinaCode, projects: state.potreeProjects || [] },
        });
      } catch (err) {
        console.warn("[pcw] Failed to save potree projects:", err);
      }
    }, 2000);
  }

  async function checkDepthLayerForMarina(code) {
    if (!code) { depthLayerAvailable = false; render(); return; }
    try {
      const resp = await fetch(`/api/depth/layer?marina=${encodeURIComponent(code)}`);
      const data = await resp.json();
      depthLayerAvailable = !!data?.rasterReady;
    } catch { depthLayerAvailable = false; }
    render();
  }

  async function loadDamLasFilesForMarina(code) {
    const marina = String(code || "").trim();
    if (!marina) return;
    try {
      status = "Loading LAS files from project DAM...";
      render();
      const payload = await requestJson(`/api/marina/assets?code=${encodeURIComponent(marina)}&category=las-files`);
      if (marina !== activeMarinaCode) return;
      const keys = (Array.isArray(payload?.files) ? payload.files : [])
        .filter((item) => /\.(las|laz)$/i.test(String(item?.name || item?.key || "")))
        .map((item) => String(item?.key || "").trim())
        .filter(Boolean);
      setProjectFilesFromDam(keys);
    } catch (err) {
      if (marina !== activeMarinaCode) return;
      state.projectFiles = [];
      state.filePath = "";
      status = `Could not load LAS files from project DAM: ${String(err?.message || err)}`;
      persist();
      render();
    }
  }

  function setActiveMarina(code) {
    const newCode = String(code || "").trim();
    if (newCode === activeMarinaCode) {
      if (activeMarinaCode) loadDamLasFilesForMarina(activeMarinaCode);
      return;
    }
    activeMarinaCode = newCode;
    lasCrsInspectRun++;
    clearTimeout(savePotreeProjectsTimer);
    state.potreeProjects = [];
    state.projectFiles = [];
    state.filePath = "";
    depthLayerAvailable = false;
    pushPotreeCommandToViewer("remove-surface", {});
    persist();
    render();
    if (activeMarinaCode) {
      loadPotreeProjects(activeMarinaCode);
      loadDamLasFilesForMarina(activeMarinaCode);
      checkDepthLayerForMarina(activeMarinaCode);
    }
  }

  function getActiveZBounds() {
    if (state.potreeBoundsManualEnabled) {
      const mn = Number(state.potreeBoundsManualMin);
      const mx = Number(state.potreeBoundsManualMax);
      if (Number.isFinite(mn) && Number.isFinite(mx) && mx > mn) {
        return { minZ: mn, maxZ: mx };
      }
    }
    const zBounds = potreeJob?.bounds || dataset?.fullBounds || dataset?.bounds || {};
    const fromObj = (obj) => {
      if (!obj || typeof obj !== "object") return null;
      const lo = Number(
        obj.minZ ?? obj.minz ?? obj.lz ??
        obj?.min?.z ?? obj?.min?.[2] ??
        obj?.z?.min ??
        obj?.bounds?.minZ ?? obj?.bounds?.minz
      );
      const hi = Number(
        obj.maxZ ?? obj.maxz ?? obj.uz ??
        obj?.max?.z ?? obj?.max?.[2] ??
        obj?.z?.max ??
        obj?.bounds?.maxZ ?? obj?.bounds?.maxz
      );
      if (Number.isFinite(lo) && Number.isFinite(hi)) return { minZ: lo, maxZ: hi };
      if (Array.isArray(obj.zRange) && obj.zRange.length >= 2) {
        const a = Number(obj.zRange[0]);
        const b = Number(obj.zRange[1]);
        if (Number.isFinite(a) && Number.isFinite(b)) return { minZ: a, maxZ: b };
      }
      return null;
    };
    const parsed = fromObj(zBounds) || fromObj(zBounds?.bounds) || fromObj(zBounds?.summary) || fromObj(zBounds?.summary?.bounds);
    let minZ = Number(parsed?.minZ);
    let maxZ = Number(parsed?.maxZ);
    if (!Number.isFinite(minZ) || !Number.isFinite(maxZ)) {
      const points = Array.isArray(dataset?.preview?.points) ? dataset.preview.points : [];
      let pMin = Number.POSITIVE_INFINITY;
      let pMax = Number.NEGATIVE_INFINITY;
      for (const p of points) {
        const z = Number(p?.z);
        if (!Number.isFinite(z)) continue;
        if (z < pMin) pMin = z;
        if (z > pMax) pMax = z;
      }
      if (Number.isFinite(pMin) && Number.isFinite(pMax)) {
        minZ = pMin;
        maxZ = pMax;
      }
    }
    if (!Number.isFinite(minZ) || !Number.isFinite(maxZ)) return { minZ: 0, maxZ: 100 };
    return { minZ: Math.min(minZ, maxZ), maxZ: Math.max(minZ, maxZ) };
  }

  function parseBoundsFromPotreeMetadataJson(parsed) {
    const bb = parsed?.tightBoundingBox || parsed?.boundingBox || parsed?.bounds || null;
    if (!bb) return null;
    const lx = Number(bb?.lx ?? bb?.min?.x ?? bb?.minx);
    const ly = Number(bb?.ly ?? bb?.min?.y ?? bb?.miny);
    const lz = Number(bb?.lz ?? bb?.min?.z ?? bb?.minz);
    const ux = Number(bb?.ux ?? bb?.max?.x ?? bb?.maxx);
    const uy = Number(bb?.uy ?? bb?.max?.y ?? bb?.maxy);
    const uz = Number(bb?.uz ?? bb?.max?.z ?? bb?.maxz);
    const ox = Number(parsed?.offset?.[0] ?? parsed?.offset?.x ?? 0);
    const oy = Number(parsed?.offset?.[1] ?? parsed?.offset?.y ?? 0);
    const oz = Number(parsed?.offset?.[2] ?? parsed?.offset?.z ?? 0);
    if (![lx, ly, lz, ux, uy, uz].every(Number.isFinite)) return null;
    return {
      minX: Math.min(lx, ux) + (Number.isFinite(ox) ? ox : 0),
      minY: Math.min(ly, uy) + (Number.isFinite(oy) ? oy : 0),
      minZ: Math.min(lz, uz) + (Number.isFinite(oz) ? oz : 0),
      maxX: Math.max(lx, ux) + (Number.isFinite(ox) ? ox : 0),
      maxY: Math.max(ly, uy) + (Number.isFinite(oy) ? oy : 0),
      maxZ: Math.max(lz, uz) + (Number.isFinite(oz) ? oz : 0),
    };
  }

  function parseBoundsFromCloudJsText(text) {
    const src = String(text || "").trim();
    if (!src) return null;
    const start = src.indexOf("{");
    const end = src.lastIndexOf("}");
    if (start < 0 || end <= start) return null;
    try {
      return parseBoundsFromPotreeMetadataJson(JSON.parse(src.slice(start, end + 1)));
    } catch {
      return null;
    }
  }

  async function hydratePotreeBoundsFromFilesIfMissing() {
    if (!potreeJob?.id) return false;
    if (potreeJob?.bounds && Number.isFinite(Number(potreeJob.bounds.minZ)) && Number.isFinite(Number(potreeJob.bounds.maxZ))) return false;
    if (potreeBoundsHydratePromise) return potreeBoundsHydratePromise;
    const jobId = String(potreeJob.id);
    const candidates = [
      `/api/point-cloud/potree/files/${encodeURIComponent(jobId)}/metadata.json`,
      `/api/point-cloud/potree/files/${encodeURIComponent(jobId)}/pointclouds/metadata.json`,
      `/api/point-cloud/potree/files/${encodeURIComponent(jobId)}/pointclouds/cloud/metadata.json`,
      `/api/point-cloud/potree/files/${encodeURIComponent(jobId)}/cloud.js`,
      `/api/point-cloud/potree/files/${encodeURIComponent(jobId)}/pointclouds/cloud.js`,
      `/api/point-cloud/potree/files/${encodeURIComponent(jobId)}/pointclouds/cloud/cloud.js`,
    ];
    potreeBoundsHydratePromise = (async () => {
      for (const url of candidates) {
        try {
          const res = await fetch(url, { method: "GET" });
          if (!res.ok) continue;
          let bounds = null;
          if (url.endsWith(".json")) {
            const parsed = await res.json();
            bounds = parseBoundsFromPotreeMetadataJson(parsed);
          } else {
            const txt = await res.text();
            bounds = parseBoundsFromCloudJsText(txt);
          }
          if (bounds && Number.isFinite(Number(bounds.minZ)) && Number.isFinite(Number(bounds.maxZ))) {
            potreeJob = { ...(potreeJob || {}), bounds };
            reconcileRangesToCurrentBounds({ resetToFull: true });
            render();
            pushPotreeStyleToViewer();
            return true;
          }
        } catch {}
      }
      return false;
    })();
    try {
      return await potreeBoundsHydratePromise;
    } finally {
      potreeBoundsHydratePromise = null;
    }
  }

  function pullStateFromForm() {
    if (!containerEl) return;
    state.potreeDatasetName = String(containerEl.querySelector("#pcw-generate-name")?.value || "").trim();
    state.rasterResolution = Math.max(0.1, Math.min(10, Number(containerEl.querySelector("#pcw-raster-resolution")?.value || state.rasterResolution || 1) || 1));
    state.rasterColorRamp = String(containerEl.querySelector("#pcw-raster-ramp")?.value || state.rasterColorRamp || "terrain");
    state.rasterStretchMin = Number(containerEl.querySelector("#pcw-raster-min")?.value ?? state.rasterStretchMin ?? 0);
    state.rasterStretchMax = Number(containerEl.querySelector("#pcw-raster-max")?.value ?? state.rasterStretchMax ?? 100);
    state.rasterOpacity = Math.max(0, Math.min(1, Number(containerEl.querySelector("#pcw-raster-opacity")?.value ?? state.rasterOpacity ?? 0.92) || 0.92));
    state.potreePointBudget = Math.max(100000, Math.min(30000000, Number(containerEl.querySelector("#pcw-potree-budget")?.value || 3000000) || 3000000));
    state.potreePointSize = Math.max(0.1, Math.min(10, Number(containerEl.querySelector("#pcw-potree-size")?.value || 0.8) || 0.8));
    state.potreeVerticalExaggeration = Math.max(1, Math.min(10, Number(containerEl.querySelector("#pcw-potree-vertical-exaggeration")?.value || state.potreeVerticalExaggeration || 1) || 1));
    state.potreePointShape = String(containerEl.querySelector("#pcw-potree-shape")?.value || "CIRCLE").toUpperCase();
    state.potreeAxisEnabled = !!containerEl.querySelector("#pcw-potree-axis-enabled")?.checked;
    state.potreeAxisTicksEnabled = !!containerEl.querySelector("#pcw-potree-axis-ticks-enabled")?.checked;
    state.potreeEdlEnabled = !!containerEl.querySelector("#pcw-potree-edl-enabled")?.checked;
    state.potreeEdlRadius = Math.max(0.1, Math.min(4, Number(containerEl.querySelector("#pcw-potree-edl-radius")?.value ?? state.potreeEdlRadius ?? 1) || 1));
    state.potreeEdlStrength = Math.max(0, Math.min(5, Number(containerEl.querySelector("#pcw-potree-edl-strength")?.value ?? state.potreeEdlStrength ?? 0.3) || 0.3));
    state.potreeEdlOpacity = Math.max(0, Math.min(1, Number(containerEl.querySelector("#pcw-potree-edl-opacity")?.value ?? state.potreeEdlOpacity ?? 1) || 1));
    state.potreeSurfaceEnabled = !!containerEl.querySelector("#pcw-potree-surface-enabled")?.checked;
    state.potreeSurfaceOpacity = Math.max(0.1, Math.min(1, Number(containerEl.querySelector("#pcw-potree-surface-opacity")?.value ?? state.potreeSurfaceOpacity ?? 0.7) || 0.7));
    state.potreeAttribute = String(containerEl.querySelector("#pcw-potree-attribute")?.value || "classification");
    state.potreeGradient = String(containerEl.querySelector("#pcw-potree-gradient")?.value || "VIRIDIS").toUpperCase();
    state.potreeBoundsManualEnabled = !!containerEl.querySelector("#pcw-potree-bounds-manual-enabled")?.checked;
    state.potreeBoundsManualMin = Number(
      containerEl.querySelector("#pcw-potree-bounds-manual-min")?.value ??
      state.potreeBoundsManualMin ??
      0
    );
    state.potreeBoundsManualMax = Number(
      containerEl.querySelector("#pcw-potree-bounds-manual-max")?.value ??
      state.potreeBoundsManualMax ??
      state.potreeBoundsManualMin ??
      0
    );
    state.potreeElevationMin = Number(state.potreeElevationMin ?? 0);
    state.potreeElevationMax = Number(state.potreeElevationMax ?? state.potreeElevationMin ?? 0);
    state.potreeZClipEnabled = !!containerEl.querySelector("#pcw-potree-zclip-enabled")?.checked;
    state.potreeZClipMin = Number(
      containerEl.querySelector("#pcw-potree-zclip-min-slider")?.value ??
      state.potreeZClipMin ??
      0
    );
    state.potreeZClipMax = Number(
      containerEl.querySelector("#pcw-potree-zclip-max-slider")?.value ??
      containerEl.querySelector("#pcw-potree-zclip-max")?.value ??
      state.potreeZClipMax ??
      state.potreeZClipMin ??
      0
    );
    for (const { id } of ASPRS_CLASSES) {
      const vis = containerEl.querySelector(`#pcw-class-vis-${id}`);
      const col = containerEl.querySelector(`#pcw-class-color-${id}`);
      if (vis) state.potreeClassVisibility[id] = !!vis.checked;
      if (col && col.value) state.potreeClassColors[id] = String(col.value);
    }
    state.epsgCode = String(containerEl.querySelector("#pcw-epsg-code")?.value || state.epsgCode || "").trim();
    state.tileZoom = Math.max(12, Math.min(21, Math.round(Number(containerEl.querySelector("#pcw-tile-zoom")?.value || state.tileZoom) || 18)));
    state.hiresResolution = Math.max(0.01, Math.min(10, Number(containerEl.querySelector("#pcw-hires-resolution")?.value || state.hiresResolution || 0.1) || 0.1));
    state.hiresMinZoom = Math.max(8, Math.min(20, Math.round(Number(containerEl.querySelector("#pcw-hires-min-zoom")?.value || state.hiresMinZoom) || 14)));
    state.hiresMaxZoom = Math.max(14, Math.min(24, Math.round(Number(containerEl.querySelector("#pcw-hires-max-zoom")?.value || state.hiresMaxZoom) || 20)));
    persist();
  }

  function currentPotreeStyle() {
    const { minZ: zMin, maxZ: zMax } = getActiveZBounds();
    const minIn = Number(state.potreeElevationMin);
    const maxIn = Number(state.potreeElevationMax);
    const eMin = Number.isFinite(minIn) ? minIn : zMin;
    const eMax = Number.isFinite(maxIn) ? Math.max(eMin, maxIn) : zMax;
    return {
      pointBudget: state.potreePointBudget,
      pointSize: state.potreePointSize,
      verticalExaggeration: state.potreeVerticalExaggeration,
      pointShape: state.potreePointShape,
      axisEnabled: state.potreeAxisEnabled !== false,
      axisTicksEnabled: state.potreeAxisTicksEnabled !== false,
      edlEnabled: !!state.potreeEdlEnabled,
      edlRadius: Number(state.potreeEdlRadius),
      edlStrength: Number(state.potreeEdlStrength),
      edlOpacity: Number(state.potreeEdlOpacity),
      attribute: state.potreeAttribute,
      gradient: state.potreeGradient,
      elevationMin: eMin,
      elevationMax: eMax,
      datasetZMin: zMin,
      datasetZMax: zMax,
      zClipEnabled: !!state.potreeZClipEnabled,
      zClipMin: Number(state.potreeZClipMin),
      zClipMax: Number(state.potreeZClipMax),
      classVisibility: { ...(state.potreeClassVisibility || {}) },
      classColors: { ...(state.potreeClassColors || {}) },
    };
  }

  function reconcileRangesToCurrentBounds({ resetToFull = false } = {}) {
    const { minZ: zMin, maxZ: zMax } = getActiveZBounds();
    const lo = Math.min(zMin, zMax);
    const hi = Math.max(zMin, zMax);
    const boundsKey = `${lo}|${hi}`;
    const boundsChanged = boundsKey !== lastRangeBoundsKey;
    if (boundsChanged) lastRangeBoundsKey = boundsKey;

    const clamp = (v, fallback) => {
      const n = Number(v);
      if (!Number.isFinite(n)) return fallback;
      return Math.max(lo, Math.min(hi, n));
    };

    if (resetToFull || boundsChanged) {
      state.potreeElevationMin = Number(oneDecimal(lo, lo));
      state.potreeElevationMax = Number(oneDecimal(hi, hi));
      state.potreeZClipMin = Number(oneDecimal(lo, lo));
      state.potreeZClipMax = Number(oneDecimal(hi, hi));
      persist();
      return;
    }

    let elevMin = clamp(state.potreeElevationMin, lo);
    let elevMax = clamp(state.potreeElevationMax, hi);
    if (elevMax < elevMin) [elevMin, elevMax] = [elevMax, elevMin];

    let clipMin = clamp(state.potreeZClipMin, lo);
    let clipMax = clamp(state.potreeZClipMax, hi);
    if (clipMax < clipMin) [clipMin, clipMax] = [clipMax, clipMin];

    state.potreeElevationMin = Number(oneDecimal(elevMin, lo));
    state.potreeElevationMax = Number(oneDecimal(elevMax, hi));
    state.potreeZClipMin = Number(oneDecimal(clipMin, lo));
    state.potreeZClipMax = Number(oneDecimal(clipMax, hi));
    persist();
  }

  function pushPotreeStyleToViewer() {
    if (!potreeJob?.id && !app?.view?.getPotreeWindow?.()) return;
    const style = currentPotreeStyle();
    const payload = {
      type: "hydrotools-potree-style",
      jobId: String(potreeJob?.id || ""),
      style,
    };
    try {
      localStorage.setItem("hydrotools:potree-style:last", JSON.stringify({ ...payload, ts: Date.now() }));
    } catch {}
    try {
      const channel = new BroadcastChannel("hydrotools-potree-controls");
      channel.postMessage(payload);
      channel.close();
    } catch {}
    try {
      if (potreeViewerWindow && !potreeViewerWindow.closed) {
        if (typeof potreeViewerWindow.__hydrotoolsApplyStyle === "function") {
          potreeViewerWindow.__hydrotoolsApplyStyle(style);
        }
        potreeViewerWindow.postMessage(payload, window.location.origin);
      }
    } catch {}
    try {
      const iframeWin = app?.view?.getPotreeWindow?.();
      if (iframeWin) {
        if (typeof iframeWin.__hydrotoolsApplyStyle === "function") {
          iframeWin.__hydrotoolsApplyStyle(style);
        }
        iframeWin.postMessage(payload, window.location.origin);
      }
    } catch {}
  }

  function pushPotreeCommandToViewer(command, args = {}) {
    if (!potreeJob?.id) return;
    const payload = {
      type: "hydrotools-potree-command",
      jobId: String(potreeJob.id),
      command: String(command || ""),
      args: args && typeof args === "object" ? args : {},
    };
    try {
      localStorage.setItem("hydrotools:potree-command:last", JSON.stringify({ ...payload, ts: Date.now() }));
    } catch {}
    try {
      const channel = new BroadcastChannel("hydrotools-potree-controls");
      channel.postMessage(payload);
      channel.close();
    } catch {}
    try {
      if (potreeViewerWindow && !potreeViewerWindow.closed) {
        if (typeof potreeViewerWindow.__hydrotoolsApplyCommand === "function") {
          potreeViewerWindow.__hydrotoolsApplyCommand(String(command || ""), payload.args);
        }
        potreeViewerWindow.postMessage(payload, window.location.origin);
      }
    } catch {}
    try {
      const iframeWin = app?.view?.getPotreeWindow?.();
      if (iframeWin) {
        if (typeof iframeWin.__hydrotoolsApplyCommand === "function") {
          iframeWin.__hydrotoolsApplyCommand(String(command || ""), payload.args);
        }
        iframeWin.postMessage(payload, window.location.origin);
      }
    } catch {}
  }

  function scorePotreeJob(job) {
    const status = String(job?.status || "").toLowerCase();
    if (status === "running" || status === "queued" || status === "prepare" || status === "convert" || status === "postprocess") return 4;
    if (status === "completed") return 3;
    if (status === "failed" || status === "canceled") return 2;
    return 1;
  }

  function buildHostedTiffUrl(filePath) {
    return `/api/qimera/local-file?path=${encodeURIComponent(String(filePath || ""))}&v=${Date.now()}`;
  }

  function addRasterLayer(kind, filePath, layerName) {
    if (!filePath) return;
    const layersService = app?.services?.layers;
    if (!layersService) return;
    const existing = (layersService.listLayers?.() || []).find((layer) =>
      String(layer?.meta?.pointCloudRasterPath || "").trim().toLowerCase() === String(filePath).toLowerCase()
    );
    const isShade = String(kind) === "hillshade";
    const stretchMin = Number(state.rasterStretchMin);
    const stretchMax = Number(state.rasterStretchMax);
    const def = {
      id: existing?.id || undefined,
      type: "geotiff-url",
      kind: "overlay",
      name: layerName,
      url: buildHostedTiffUrl(filePath),
      opacity: existing?.opacity ?? state.rasterOpacity ?? 0.92,
      visible: true,
      meta: {
        sourceType: "point-cloud-workspace",
        rasterKind: kind,
        pointCloudRasterPath: filePath,
      },
      options: {
        autoZoom: kind === "hillshade",
        colorRamp: isShade ? "grayscale" : String(state.rasterColorRamp || "terrain").toLowerCase(),
        stretchMin: Number.isFinite(stretchMin) ? stretchMin : undefined,
        stretchMax: Number.isFinite(stretchMax) ? stretchMax : undefined,
      },
    };
    if (existing?.id) layersService.removeLayer(existing.id);
    layersService.addLayer("point-cloud-workspace", def);
  }

  function applyRasterStyleToGeneratedLayers() {
    const layersService = app?.services?.layers;
    if (!layersService) return;
    pullStateFromForm();
    const layers = layersService.listLayers?.() || [];
    const targets = layers.filter((layer) => String(layer?.meta?.sourceType || "") === "point-cloud-workspace");
    for (const layer of targets) {
      const kind = String(layer?.meta?.rasterKind || "");
      const filePath = String(layer?.meta?.pointCloudRasterPath || "");
      if (!filePath) continue;
      const name = String(layer?.name || kind || "Raster");
      if (layer?.id) layersService.removeLayer(layer.id);
      addRasterLayer(kind, filePath, name);
    }
    status = `Applied raster style to ${targets.length} layer(s).`;
    render();
  }

  async function generateAndLoad2DLayers() {
    pullStateFromForm();
    const filePath = String(state.filePath || "").trim();
    if (!filePath) {
      status = "Open a LAS/LAZ dataset first.";
      render();
      return;
    }
    potreeBusy = true;
    status = "Generating DSM/DTM/Hillshade rasters...";
    render();
    try {
      const payload = await requestJson("/api/image-generator/las-products", {
        method: "POST",
        body: {
          filePath,
          resolution: Number(state.rasterResolution || 1),
          minAboveWater: 0.5,
          maxAboveWater: 6,
        },
      });
      const products = payload?.products || {};
      const stem = String(state.potreeDatasetName || dataset?.format || "Point Cloud");
      addRasterLayer("hillshade", products.hillshade, `${stem} Hillshade`);
      addRasterLayer("dsm", products.dsm || products.elevation, `${stem} DSM`);
      addRasterLayer("dtm", products.dtm, `${stem} DTM`);
      status = "Loaded DSM/DTM/Hillshade as map layers.";
    } catch (error) {
      status = `Could not generate/load 2D layers: ${String(error?.message || error)}`;
    } finally {
      potreeBusy = false;
      render();
    }
  }

  async function findMatchingPotreeJob() {
    const payload = await requestJson("/api/point-cloud/potree/jobs");
    const jobs = Array.isArray(payload?.jobs) ? payload.jobs : [];
    if (!jobs.length) return null;
    const sourcePath = String(state.filePath || "").trim().toLowerCase();
    const datasetName = String(state.potreeDatasetName || "").trim().toLowerCase();
    const lastId = String(state.potreeLastJobId || "").trim();
    let candidates = jobs;
    if (lastId) {
      const byId = jobs.find((j) => String(j?.id || "") === lastId);
      if (byId) {
        const byIdSource = String(byId?.sourcePath || "").trim().toLowerCase();
        // Only trust remembered job id when it matches the currently selected source.
        if (!sourcePath || (byIdSource && byIdSource === sourcePath)) return byId;
      }
    }
    if (sourcePath) {
      const byPath = jobs.filter((j) => String(j?.sourcePath || "").trim().toLowerCase() === sourcePath);
      if (byPath.length) candidates = byPath;
    } else if (datasetName) {
      const byName = jobs.filter((j) => String(j?.datasetName || "").trim().toLowerCase() === datasetName);
      if (byName.length) candidates = byName;
    }
    candidates.sort((a, b) => {
      const ds = scorePotreeJob(b) - scorePotreeJob(a);
      if (ds) return ds;
      const ta = Date.parse(String(a?.updatedAt || a?.createdAt || 0)) || 0;
      const tb = Date.parse(String(b?.updatedAt || b?.createdAt || 0)) || 0;
      return tb - ta;
    });
    return candidates[0] || null;
  }

  async function refreshOrAttachPotreeJob() {
    if (potreeJob?.id) {
      await refreshPotreeJob();
      return;
    }
    try {
      const matched = await findMatchingPotreeJob();
      if (!matched) {
        // No job on server — if we know the last job ID, reconstruct a minimal stub
        // so the Open Viewer button becomes available (server serves files from S3).
        const lastId = String(state.potreeLastJobId || "").trim();
        if (lastId) {
          potreeJob = {
            id: lastId, status: "completed", progressPct: 100, stage: "done",
            datasetName: state.potreeDatasetName || "Point Cloud",
            sourcePaths: [state.filePath || ""].filter(Boolean), message: "Recovered from last session.",
            viewerUrl: `/api/point-cloud/potree/files/${encodeURIComponent(lastId)}/viewer.html`,
          };
          persist();
          status = "Potree viewer recovered — click Open Viewer.";
          render();
          return;
        }
        status = "No matching Potree job found for current dataset.";
        render();
        return;
      }
      potreeJob = matched;
      state.potreeLastJobId = String(matched.id || "");
      persist();
      await hydratePotreeBoundsFromFilesIfMissing();
      const jobStatus = String(matched?.status || "").toLowerCase();
      if (!["completed", "failed", "canceled"].includes(jobStatus)) {
        stopPotreePoll();
        potreePollTimer = setInterval(refreshPotreeJob, 1500);
      }
      await refreshPotreeJob();
    } catch (error) {
      status = String(error?.message || error || "Could not refresh Potree job.");
      render();
    }
  }

  function upsertProjectFile(entry) {
    const src = String(entry?.sourcePath || "").trim();
    if (!src) return;
    const existingIdx = (state.projectFiles || []).findIndex((item) =>
      String(item?.sourcePath || "").trim().toLowerCase() === src.toLowerCase()
    );
    const nextItem = {
      id: String(entry?.id || makeProjectItemId()),
      name: String(entry?.name || src.split(/[\\/]/).pop() || "Point Cloud"),
      sourcePath: src,
      potreeJobId: String(entry?.potreeJobId || ""),
      status: String(entry?.status || "ready"),
      progressPct: Number.isFinite(Number(entry?.progressPct)) ? Number(entry.progressPct) : 0,
      viewerUrl: String(entry?.viewerUrl || ""),
      selected: entry?.selected !== false,
      crsInspection: entry?.crsInspection && typeof entry.crsInspection === "object" ? entry.crsInspection : null,
    };
    if (existingIdx >= 0) {
      // preserve existing selection state unless caller explicitly sets it
      const existingSelected = state.projectFiles[existingIdx].selected;
      state.projectFiles[existingIdx] = {
        ...state.projectFiles[existingIdx],
        ...nextItem,
        selected: entry?.selected !== undefined ? entry.selected : existingSelected !== undefined ? existingSelected : true,
      };
    } else {
      state.projectFiles = [...(state.projectFiles || []), nextItem];
    }
    persist();
  }

  function removeProjectFile(itemId) {
    const id = String(itemId || "");
    state.projectFiles = (state.projectFiles || []).filter((item) => String(item?.id || "") !== id);
    persist();
  }

  function setProjectFileCrsInspection(itemId, patch) {
    const id = String(itemId || "");
    let changed = false;
    state.projectFiles = (state.projectFiles || []).map((item) => {
      if (String(item?.id || "") !== id) return item;
      changed = true;
      return { ...item, crsInspection: { ...(item.crsInspection || {}), ...patch } };
    });
    if (changed) persist();
  }

  async function inspectProjectFileCrs(itemId, runId = lasCrsInspectRun) {
    const item = (state.projectFiles || []).find((entry) => String(entry?.id || "") === String(itemId || ""));
    const sourcePath = String(item?.sourcePath || "").trim();
    if (!item || !sourcePath || !isProjectS3Path(sourcePath) || !/\.(las|laz)$/i.test(sourcePath)) return null;
    setProjectFileCrsInspection(item.id, { status: "inspecting", ok: true, inspectedAt: "" });
    render();
    try {
      const payload = await requestJson("/api/point-cloud/inspect-las-crs", {
        method: "POST",
        body: { sourcePath },
      });
      if (runId !== lasCrsInspectRun) return null;
      setProjectFileCrsInspection(item.id, { ...payload, status: "done", ok: payload?.ok !== false });
      const detected = normalizeEpsg(payload?.epsg || payload?.epsgCodes?.[0]);
      if (detected) {
        const projectEpsg = normalizeEpsg(state.epsgCode);
        status = projectEpsg && detected !== projectEpsg
          ? `Detected ${detected} in ${item.name}; project is ${projectEpsg}.`
          : `Detected ${detected} in ${item.name}.`;
      } else {
        status = `No embedded EPSG found in ${item.name}.`;
      }
      persist();
      render();
      return payload;
    } catch (err) {
      if (runId !== lasCrsInspectRun) return null;
      const message = String(err?.message || err || "LAS CRS inspection failed");
      setProjectFileCrsInspection(item.id, { status: "failed", ok: false, error: message, inspectedAt: new Date().toISOString() });
      status = `CRS inspection failed for ${item.name}: ${message}`;
      persist();
      render();
      return null;
    }
  }

  async function inspectProjectFileCrsBatch(items = state.projectFiles || []) {
    const runId = ++lasCrsInspectRun;
    const targets = (items || []).filter((item) => {
      const sourcePath = String(item?.sourcePath || "").trim();
      return sourcePath && isProjectS3Path(sourcePath) && /\.(las|laz)$/i.test(sourcePath);
    });
    for (const item of targets) {
      if (runId !== lasCrsInspectRun) return;
      await inspectProjectFileCrs(item.id, runId);
    }
  }

  async function addProjectFilesFromPicker() {
    const files = [];
    if (!files.length) return;
    potreeBusy = true;
    status = `Uploading ${files.length} file(s) to project...`;
    render();
    try {
      for (let i = 0; i < files.length; i += 1) {
        const file = files[i];
        const isXyz = /\.(xyz|csv|pts)$/i.test(file?.name || "") ||
          (/\.txt$/i.test(file?.name || "") && !/\.(las|laz)$/i.test(file?.name || ""));
        status = `${isXyz ? "Converting" : "Uploading"} ${i + 1}/${files.length}: ${String(file?.name || "point-cloud.las")}`;
        taskStatus(`${isXyz ? "Converting XYZ" : "Uploading LAS"}: ${String(file?.name || "")}`, null);
        render();
        let sourcePath, outName;
        if (isXyz) {
          const res = await fetch("/api/point-cloud/convert-xyz", {
            method: "POST",
            headers: { "Content-Type": "application/octet-stream", "x-file-name": String(file.name) },
            body: file,
          });
          const body = await res.json().catch(() => ({}));
          if (!res.ok || !body?.sourcePath) throw new Error(String(body?.error || "xyz_convert_failed"));
          sourcePath = body.sourcePath;
          outName = String(body.fileName || file.name.replace(/\.(xyz|txt|csv|pts)$/i, ".laz"));
        } else {
          const res = await fetch("/api/point-cloud/potree/upload", {
            method: "POST",
            headers: { "Content-Type": "application/octet-stream", "x-file-name": String(file?.name || "point-cloud.las") },
            body: file,
          });
          const body = await res.json().catch(() => ({}));
          if (!res.ok || !body?.sourcePath) throw new Error(String(body?.error || "potree_upload_failed"));
          sourcePath = body.sourcePath;
          outName = String(file?.name || "");
        }
        upsertProjectFile({ name: outName, sourcePath, status: "ready", selected: true });
      }
      status = `Added ${files.length} file(s) to project.`;
      taskIdle();
    } catch (error) {
      status = `Could not add project files: ${String(error?.message || error)}`;
      taskIdle();
    } finally {
      potreeBusy = false;
      if (input) input.value = "";
      render();
    }
  }

  async function generatePotreeForProjectFiles() {
    const all = (state.projectFiles || []).filter((item) => String(item?.sourcePath || "").trim());
    const checked = all.filter((item) => item.selected !== false);
    const items = checked.length ? checked : all;
    if (!items.length) {
      status = "Add LAS/LAZ files to the project first.";
      render();
      return;
    }
    potreeBusy = true;
    projectAutoOpenFirstCompleted = true;
    startProjectJobsPoll();
    status = `Queueing Potree jobs for ${items.length} file(s)...`;
    render();
    try {
      for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        status = `Queueing ${i + 1}/${items.length}: ${item.name}`;
        render();
        const startRes = await requestJson("/api/point-cloud/potree/jobs", {
          method: "POST",
          body: {
            datasetName: String(item.name || `Point Cloud ${i + 1}`),
            sourcePath: String(item.sourcePath || ""),
            marina: activeMarinaCode || undefined,
          },
        });
        const jobId = String(startRes?.job?.id || "");
        if (jobId) app.log("potree.job_started", { datasetName: item.name, marina: activeMarinaCode });
        upsertProjectFile({
          ...item,
          potreeJobId: jobId,
          status: jobId ? "queued" : "failed",
        });
      }
      status = "Potree jobs queued for project files.";
      await refreshProjectFileJobStatuses();
    } catch (error) {
      status = `Could not queue Potree jobs: ${String(error?.message || error)}`;
    } finally {
      potreeBusy = false;
      render();
    }
  }

  function suggestCombinedDatasetName(items = []) {
    const explicit = String(state.potreeDatasetName || "").trim();
    if (explicit) return explicit;
    const validItems = Array.isArray(items) ? items : [];
    if (!validItems.length) return "Combined Point Cloud";
    const firstName = String(validItems[0]?.name || validItems[0]?.sourcePath || "Point Cloud");
    const stem = firstName.replace(/\.(las|laz)$/i, "").trim() || "Point Cloud";
    return validItems.length > 1 ? `${stem} Combined` : stem;
  }

  async function generateCombinedPotreeForProjectFiles() {
    const all = (state.projectFiles || []).filter((item) => String(item?.sourcePath || "").trim());
    const checked = all.filter((item) => item.selected !== false);
    const items = checked.length ? checked : all;
    if (!items.length) {
      status = "No LAS files loaded from DAM.";
      render();
      return;
    }
    const sourcePaths = items.map((item) => String(item?.sourcePath || "").trim()).filter(Boolean);
    const datasetName = String(state.potreeDatasetName || "").trim() || suggestCombinedDatasetName(items);

    // Add a pending project entry immediately so user sees it
    const projEntryId = makeProjectItemId();
    state.potreeProjects = [...(state.potreeProjects || []), {
      id: projEntryId, name: datasetName, jobId: "", status: "queued",
      progressPct: 0, viewerUrl: "", message: "Starting…",
      createdAt: Date.now(), sourcePaths,
    }];
    savePotreeProjects();

    potreeBusy = true;
    potreeJob = { id: "", status: "preparing", progressPct: 8, message: `Preparing ${sourcePaths.length} file(s)…` };
    status = `Queueing Potree job for ${sourcePaths.length} file(s)…`;
    render();
    try {
      const startRes = await requestJson("/api/point-cloud/potree/jobs", {
        method: "POST",
        body: { datasetName, sourcePaths, marina: activeMarinaCode || undefined },
      });
      if (!startRes?.job?.id) throw new Error("potree_combined_job_start_failed");
      potreeJob = startRes.job;
      const jobId = String(startRes.job.id || "");
      app.log("potree.job_started", { datasetName, count: sourcePaths.length, marina: activeMarinaCode });
      state.potreeLastJobId = jobId;
      if (!state.filePath && sourcePaths[0]) state.filePath = String(sourcePaths[0]);
      // Link the potree project entry to the real job ID
      state.potreeProjects = (state.potreeProjects || []).map((p) =>
        p.id === projEntryId ? { ...p, jobId, status: "running" } : p
      );
      savePotreeProjects();
      persist();
      stopPotreePoll();
      potreePollTimer = setInterval(refreshPotreeJob, 1200);
      await refreshPotreeJob();
      status = `Potree conversion running — watch Potree Projects for progress.`;
    } catch (error) {
      potreeBusy = false;
      potreeJob = { ...(potreeJob || {}), status: "failed", progressPct: 100, error: String(error?.message || error), message: "Generation failed." };
      state.potreeProjects = (state.potreeProjects || []).map((p) =>
        p.id === projEntryId ? { ...p, status: "failed", message: String(error?.message || error) } : p
      );
      savePotreeProjects();
      status = `Could not start Potree job: ${String(error?.message || error)}`;
      render();
    }
  }

  async function refreshProjectFileJobStatuses() {
    try {
      const payload = await requestJson("/api/point-cloud/potree/jobs");
      const jobs = Array.isArray(payload?.jobs) ? payload.jobs : [];
      const byId = new Map(jobs.map((j) => [String(j?.id || ""), j]));
      let firstCompletedViewerUrl = "";
      let hasActive = false;
      state.projectFiles = (state.projectFiles || []).map((item) => {
        const jobId = String(item?.potreeJobId || "");
        if (!jobId) return item;
        const job = byId.get(jobId);
        const nextStatus = String(job?.status || item?.status || "queued");
        if (["running", "queued", "prepare", "convert", "postprocess"].includes(nextStatus)) hasActive = true;
        const progressPct = Number(job?.progressPct);
        const derivedViewerUrl = jobId
          ? `/api/point-cloud/potree/files/${encodeURIComponent(jobId)}/viewer.html` : "";
        const viewerUrl = String(job?.viewerUrl || item?.viewerUrl || derivedViewerUrl);
        if (!firstCompletedViewerUrl && nextStatus === "completed" && viewerUrl) {
          firstCompletedViewerUrl = viewerUrl;
        }
        return {
          ...item,
          status: nextStatus,
          progressPct: Number.isFinite(progressPct) ? progressPct : (Number(item?.progressPct) || 0),
          viewerUrl,
          message: String(job?.message || item?.message || ""),
        };
      });
      persist();
      render();
      if (!hasActive) stopProjectJobsPoll();
      if (projectAutoOpenFirstCompleted && firstCompletedViewerUrl) {
        projectAutoOpenFirstCompleted = false;
        openPotreeViewer(firstCompletedViewerUrl);
      }
    } catch {}
  }

  function openPotreeViewer(url) {
    const viewerUrl = String(url || "").trim();
    if (!viewerUrl) return;
    if (app?.view?.showPotree) {
      app.view.showPotree(viewerUrl);
      potreeViewerWindow = app?.view?.getPotreeWindow?.() || null;
    } else {
      potreeViewerWindow = window.open(viewerUrl, "_blank");
    }
    setTimeout(() => {
      potreeViewerWindow = app?.view?.getPotreeWindow?.() || potreeViewerWindow;
      pushPotreeStyleToViewer();
    }, 700);
  }

  function formatNumber(value, digits = 2) {
    const n = Number(value);
    if (!Number.isFinite(n)) return "";
    return n.toLocaleString(undefined, { maximumFractionDigits: digits });
  }

  function projectJobIcon(item) {
    const status = String(item?.status || "").toLowerCase();
    const pct = Number(item?.progressPct);
    if (status === "failed" || status === "canceled") {
      return `<span style="color:#ef4444; font-weight:700;" title="${esc(status)}">✕</span>`;
    }
    if (status === "completed") {
      return `<span style="color:#22c55e; font-weight:700;" title="Completed">✓</span>`;
    }
    if (Number.isFinite(pct) && pct <= 25) {
      return `<span style="color:#38bdf8; font-weight:700;" title="Downloading from S3">↓</span>`;
    }
    if (Number.isFinite(pct) && pct < 88) {
      return `<span style="color:#f59e0b; font-weight:700;" title="Converting">◔</span>`;
    }
    if (Number.isFinite(pct) && pct < 100) {
      return `<span style="color:#38bdf8; font-weight:700;" title="Uploading to S3">↑</span>`;
    }
    if (status === "queued" || status === "prepare" || status === "running") {
      return `<span style="color:#94a3b8; font-weight:700;" title="${esc(status || "Queued")}">…</span>`;
    }
    return `<span style="color:#94a3b8; font-weight:700;" title="Ready">•</span>`;
  }

  function renderLasFileItem(item) {
    const checked = item.selected !== false;
    const crs = item?.crsInspection && typeof item.crsInspection === "object" ? item.crsInspection : null;
    const currentEpsg = normalizeEpsg(state.epsgCode);
    const detectedEpsg = normalizeEpsg(crs?.epsg || crs?.epsgCodes?.[0]);
    const mismatch = crs?.ok !== false && currentEpsg && detectedEpsg && currentEpsg !== detectedEpsg;
    const badge = renderLasCrsBadge(crs, mismatch);
    return `<div style="margin-bottom:6px; padding:5px 6px; border:1px solid rgba(148,163,184,.14); border-radius:7px; background:rgba(2,6,23,.22);">
      <div style="display:flex; align-items:center; gap:6px; min-width:0;">
        <input type="checkbox" data-project-select="${esc(item.id)}" ${checked ? "checked" : ""} style="flex-shrink:0; margin:0; cursor:pointer;" />
        <div class="small" style="flex:1; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; min-width:0; cursor:pointer;" title="${esc(item.sourcePath)}">${esc(item.name)}</div>
        <button class="btn" data-project-inspect="${esc(item.id)}" title="Detect LAS CRS" style="flex-shrink:0; padding:2px 7px; font-size:10px;">CRS</button>
        <button class="btn" data-project-remove="${esc(item.id)}" style="flex-shrink:0; padding:2px 8px; font-size:11px;">×</button>
      </div>
      ${badge}
    </div>`;
  }

  function normalizeEpsg(value) {
    const m = String(value || "").trim().match(/(?:EPSG[:\s-]*)?(\d{3,6})/i);
    return m ? `EPSG:${m[1]}` : "";
  }

  function renderLasCrsBadge(crs, mismatch) {
    if (!crs) {
      return `<div class="small" style="margin-top:4px; font-size:10px; color:rgba(148,163,184,.72);">CRS not inspected yet.</div>`;
    }
    if (crs.status === "inspecting") {
      return `<div class="small" style="margin-top:4px; font-size:10px; color:#38bdf8;">Detecting embedded LAS CRS…</div>`;
    }
    if (crs.ok === false) {
      return `<div class="small" style="margin-top:4px; font-size:10px; color:#fca5a5;" title="${esc(crs.error || "")}">CRS inspection failed.</div>`;
    }
    const detected = normalizeEpsg(crs.epsg || crs.epsgCodes?.[0]);
    if (!detected) {
      return `<div class="small" style="margin-top:4px; font-size:10px; color:#fbbf24;">No embedded EPSG found in LAS metadata.</div>`;
    }
    const hUnit = String(crs?.units?.horizontal || "").trim();
    const vUnit = String(crs?.units?.vertical || "").trim();
    const vertical = String(crs?.verticalName || crs?.vertical || "").trim();
    const extra = [hUnit ? `H ${hUnit}` : "", vUnit ? `V ${vUnit}` : "", vertical ? vertical : ""].filter(Boolean).join(" · ");
    const color = mismatch ? "#fbbf24" : "#86efac";
    const suffix = mismatch ? ` · Project is ${normalizeEpsg(state.epsgCode)}` : "";
    return `<div class="small" style="margin-top:4px; font-size:10px; color:${color}; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;" title="${esc([detected, extra, suffix].filter(Boolean).join(" "))}">
      Detected ${esc(detected)}${extra ? ` · ${esc(extra)}` : ""}${suffix ? ` · ${esc(suffix.replace(/^ · /, ""))}` : ""}
    </div>`;
  }

  function renderPotreeProjectItem(proj) {
    const st = String(proj.status || "queued").toLowerCase();
    const isCompleted = st === "completed";
    const isFailed = st === "failed" || st === "canceled";
    const isRunning = !isCompleted && !isFailed;
    const pct = Math.max(0, Math.min(100, Number(proj.progressPct) || 0));
    const isActive = potreeJob?.id === proj.jobId && !!proj.jobId;
    const viewerUrl = proj.viewerUrl || (proj.jobId ? `/api/point-cloud/potree/files/${encodeURIComponent(proj.jobId)}/viewer.html` : "");
    return `<div style="margin-bottom:8px; padding:8px; border:1px solid rgba(148,163,184,${isActive ? ".35" : ".15"}); border-radius:8px; background:rgba(2,6,23,${isActive ? ".55" : ".3"});">
      <div style="display:flex; align-items:center; gap:6px;">
        ${projectJobIcon(proj)}
        <div class="small" style="flex:1; font-weight:600; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;" title="${esc(proj.name)}">${esc(proj.name)}</div>
        <button class="btn"${isCompleted || (viewerUrl && !isRunning) ? "" : " disabled"} data-potree-project-open="${esc(proj.jobId)}" style="flex-shrink:0; font-size:11px;">Open Viewer</button>
        <button class="btn" data-potree-project-delete="${esc(proj.id)}" style="flex-shrink:0; padding:2px 8px; font-size:11px;">×</button>
      </div>
      ${isRunning && pct > 0 ? `<div style="margin-top:6px; height:4px; border-radius:2px; background:rgba(255,255,255,.1); overflow:hidden;"><div style="height:100%; width:${Math.max(2, pct)}%; background:linear-gradient(90deg,#38bdf8,#22c55e); border-radius:2px;"></div></div>` : ""}
      ${proj.message ? `<div class="small" style="margin-top:3px; opacity:.55; font-size:10px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; color:${isFailed ? "#fca5a5" : "inherit"};">${esc(proj.message)}</div>` : ""}
    </div>`;
  }

  function gradientStops(name) {
    const key = String(name || "VIRIDIS").toUpperCase();
    if (key === "COPPER") return ["#1f1300", "#ffb347"];
    if (key === "BLUE_ORANGE") return ["#0b3b8c", "#f59e0b"];
    if (key === "ICE_FIRE") return ["#dbeafe", "#dc2626"];
    if (key === "INFERNO") return ["#000004", "#420a68", "#932567", "#dd513a", "#fca50a", "#fcffa4"];
    if (key === "PLASMA") return ["#0d0887", "#6a00a8", "#b12a90", "#e16462", "#fca636", "#f0f921"];
    if (key === "SPECTRAL") return ["#5e4fa2", "#3288bd", "#66c2a5", "#abdda4", "#e6f598", "#fdae61", "#f46d43", "#9e0142"];
    if (key === "RAINBOW") return ["#6e40aa", "#3b8eea", "#29c7ac", "#8bd646", "#f9cf3a", "#f08228", "#da2f47"];
    if (key === "GRAYSCALE") return ["#111827", "#f8fafc"];
    return ["#440154", "#3b528b", "#21918c", "#5ec962", "#fde725"];
  }

  function gradientCss(name) {
    const stops = gradientStops(name);
    return `linear-gradient(90deg, ${stops.join(", ")})`;
  }

  function potreeGradientToColorRamp(gradient) {
    const g = String(gradient || "VIRIDIS").toUpperCase();
    if (g === "INFERNO") return "inferno";
    if (g === "PLASMA") return "plasma";
    if (g === "SPECTRAL" || g === "ICE_FIRE") return "spectral";
    if (g === "RAINBOW") return "terrain";
    if (g === "GRAYSCALE") return "grayscale";
    if (g === "COPPER") return "copper";
    if (g === "BLUE_ORANGE") return "blue_orange";
    return "viridis";
  }

  function stopTiffExportPoll() {
    if (tiffExportPollTimer) { clearInterval(tiffExportPollTimer); tiffExportPollTimer = null; }
  }

  function addTiffLayer(style, filePath, wgs84Bounds, proj4Def) {
    const layersService = app?.services?.layers;
    if (!layersService) return;
    // Remove any previous PDAL export layers for this tool before adding the new one
    const existing = layersService.listLayers().filter((l) => l.meta?.pclExport);
    for (const l of existing) layersService.removeLayer(l.id);
    const stem = String(state.potreeDatasetName || state.filePath?.split("/").pop() || "Point Cloud");
    const attrLabel = style.attribute === "intensity" ? "Intensity" : "Elevation";
    // wgs84Bounds from server: [west, south, east, north]
    // geographicBounds option expects: [west, east, south, north]
    const hasWgs84 = Array.isArray(wgs84Bounds) && wgs84Bounds.length === 4
      && wgs84Bounds.every((v) => Number.isFinite(Number(v)));
    const geographicBounds = hasWgs84
      ? [wgs84Bounds[0], wgs84Bounds[2], wgs84Bounds[1], wgs84Bounds[3]]
      : undefined;
    layersService.addLayer("point-cloud-workspace", {
      type: "geotiff-url",
      kind: "overlay",
      name: `${stem} ${attrLabel} (PDAL Export)`,
      url: buildHostedTiffUrl(filePath),
      opacity: state.rasterOpacity ?? 0.92,
      visible: true,
      meta: {
        sourceType: "point-cloud-workspace", rasterKind: "pdal-export",
        pointCloudRasterPath: filePath, pclExport: true,
        bounds: hasWgs84 ? wgs84Bounds : undefined,
      },
      options: {
        colorRamp: potreeGradientToColorRamp(style.gradient),
        stretchMin: Number.isFinite(style.elevationMin) ? style.elevationMin : undefined,
        stretchMax: Number.isFinite(style.elevationMax) ? style.elevationMax : undefined,
        ...(geographicBounds ? { geographicBounds } : proj4Def ? { projectionCode: proj4Def } : {}),
      },
    });
  }

  async function exportAndLoadTiff() {
    pullStateFromForm();
    const filePath = String(state.filePath || "").trim();
    if (!filePath) { status = "Open a LAS/LAZ dataset first."; render(); return; }

    stopTiffExportPoll();
    tiffExportBusy = true;
    tiffExportProgress = 0;
    tiffExportMessage = "Starting export...";
    render();

    const style = currentPotreeStyle();
    let jobId = null;

    try {
      const start = await requestJson("/api/point-cloud/export-tiff", {
        method: "POST",
        body: {
          sourcePath: filePath,
          attribute: style.attribute,
          gradient: style.gradient,
          elevationMin: style.elevationMin,
          elevationMax: style.elevationMax,
          zClipEnabled: style.zClipEnabled,
          zClipMin: style.zClipMin,
          zClipMax: style.zClipMax,
          resolution: Number(state.rasterResolution || 1),
          datasetName: String(state.potreeDatasetName || "").trim() || filePath.split("/").pop(),
        },
      });
      if (!start?.ok || !start?.jobId) throw new Error(start?.error || "failed to start export job");
      jobId = start.jobId;
    } catch (error) {
      tiffExportBusy = false;
      status = `GeoTIFF export failed: ${String(error?.message || error)}`;
      render();
      return;
    }

    // Poll for progress
    tiffExportPollTimer = setInterval(async () => {
      try {
        const res = await requestJson(`/api/point-cloud/export-tiff/jobs/${jobId}`);
        const job = res?.job;
        if (!job) return;
        tiffExportProgress = Number(job.progressPct) || 0;
        tiffExportMessage = String(job.message || "");
        render();

        if (job.status === "completed") {
          stopTiffExportPoll();
          tiffExportBusy = false;
          if (job.filePath) addTiffLayer(style, job.filePath, job.wgs84Bounds, job.proj4Def);
          status = "GeoTIFF exported and loaded as a map layer.";
          tiffExportProgress = 100;
          render();
        } else if (job.status === "failed") {
          stopTiffExportPoll();
          tiffExportBusy = false;
          status = `GeoTIFF export failed: ${job.error || job.message}`;
          tiffExportProgress = 0;
          render();
        }
      } catch { /* poll errors are transient, keep trying */ }
    }, 1500);
  }

  function addTileLayer(jobId, tileUrl, wgs84Bounds, zoom, minNativeZoom) {
    const layersService = app?.services?.layers;
    if (!layersService) return;
    const existing = layersService.listLayers().filter((l) => l.meta?.pclExport);
    for (const l of existing) layersService.removeLayer(l.id);
    const stem = String(state.potreeDatasetName || state.filePath?.split("/").pop() || "Point Cloud");
    const style = currentPotreeStyle();
    const attrLabel = style?.attribute === "intensity" ? "Intensity" : "Elevation";
    layersService.addLayer("point-cloud-workspace", {
      type: "tile-url",
      kind: "overlay",
      name: `${stem} ${attrLabel} (Tiles)`,
      url: tileUrl,
      opacity: state.rasterOpacity ?? 0.92,
      visible: true,
      meta: { sourceType: "point-cloud-workspace", rasterKind: "tile-export", pclExport: true, tileJobId: jobId },
      options: { bounds: wgs84Bounds, maxNativeZoom: zoom, minNativeZoom: minNativeZoom || 14, maxZoom: 26, autoZoom: true },
    });
  }

  function addTileLayerFromDam(entry) {
    const layersService = app?.services?.layers;
    if (!layersService || !entry?.tileUrl || !entry?.jobId) return;
    // Skip if this jobId is already on the map
    const already = layersService.listLayers().some((l) => l.meta?.tileJobId === entry.jobId);
    if (already) return;
    const attrLabel = String(entry.attribute || "elevation") === "intensity" ? "Intensity" : "Elevation";
    const name = String(entry.name || entry.datasetName || entry.jobId || "Tileset");
    layersService.addLayer("point-cloud-workspace", {
      type: "tile-url",
      kind: "overlay",
      name: `${name} (${attrLabel})`,
      url: String(entry.tileUrl),
      opacity: state.rasterOpacity ?? 0.92,
      visible: true,
      meta: { sourceType: "point-cloud-workspace", rasterKind: "tile-export", pclExport: true, tileJobId: entry.jobId },
      options: {
        bounds: entry.wgs84Bounds || null,
        maxNativeZoom: Number(entry.zoom || entry.maxNativeZoom || 18),
        minNativeZoom: Number(entry.minNativeZoom || 14),
        maxZoom: 26,
        autoZoom: true,
      },
    });
  }

  function removeTilesetFromMap(jobId) {
    const layersService = app?.services?.layers;
    if (!layersService || !jobId) return;
    const targets = layersService.listLayers().filter((l) => l.meta?.tileJobId === jobId);
    for (const l of targets) layersService.removeLayer(l.id);
  }

  async function deleteTileJob(kind) {
    const jobId = kind === "hires" ? lastHiresJobId : lastTileJobId;
    if (!jobId) return;
    try {
      await requestJson(`/api/point-cloud/tile-jobs/${jobId}`, { method: "DELETE" });
      if (kind === "hires") lastHiresJobId = null;
      else lastTileJobId = null;
      status = `Tile files deleted from VM disk (${jobId}).`;
    } catch (err) {
      status = `Delete failed: ${String(err?.message || err)}`;
    }
    render();
  }

  async function generateAndLoadTiles() {
    pullStateFromForm();
    const filePath = String(state.filePath || "").trim();
    if (!filePath) { status = "Open a LAS/LAZ dataset first."; render(); return; }
    const epsgCode = String(state.epsgCode || "").trim();
    if (!epsgCode) { status = "Enter the source EPSG code (e.g. 2227) first."; render(); return; }

    stopTiffExportPoll();
    tiffExportBusy = true;
    tiffExportProgress = 0;
    tiffExportMessage = "Starting tile generation...";
    render();

    const style = currentPotreeStyle();
    let jobId = null;

    try {
      const start = await requestJson("/api/point-cloud/generate-tiles", {
        method: "POST",
        body: {
          sourcePath: filePath,
          sourceEpsg: epsgCode,
          zoom: state.tileZoom,
          attribute: style.attribute,
          colorRamp: potreeGradientToColorRamp(style.gradient),
          stretchMin: Number.isFinite(style.elevationMin) ? style.elevationMin : undefined,
          stretchMax: Number.isFinite(style.elevationMax) ? style.elevationMax : undefined,
          zClipEnabled: style.zClipEnabled,
          zClipMin: style.zClipMin,
          zClipMax: style.zClipMax,
          datasetName: String(state.potreeDatasetName || "").trim() || filePath.split("/").pop(),
          marina: activeMarinaCode || undefined,
        },
      });
      if (!start?.ok || !start?.jobId) throw new Error(start?.error || "failed to start tile job");
      jobId = start.jobId;
      lastTileJobId = jobId;
      app.log("tiles.generation_started", { marina: activeMarinaCode, zoom: state.tileZoom });
    } catch (error) {
      tiffExportBusy = false;
      status = `Tile generation failed: ${String(error?.message || error)}`;
      render();
      return;
    }

    taskStatus("Tile generation starting…", 0);
    tiffExportPollTimer = setInterval(async () => {
      try {
        const res = await requestJson(`/api/point-cloud/generate-tiles/jobs/${jobId}`);
        const job = res?.job;
        if (!job) return;
        tiffExportProgress = Number(job.progressPct) || 0;
        tiffExportMessage = String(job.message || "");
        tileExportJob = job;
        tileExportKind = "tiles";
        taskStatus(job.message || job.stage || "Generating tiles…", tiffExportProgress);
        render();

        if (job.status === "completed") {
          stopTiffExportPoll();
          tiffExportBusy = false;
          tileExportJob = null; tileExportKind = "";
          taskIdle();
          if (job.tileUrl) addTileLayer(jobId, job.tileUrl, job.wgs84Bounds, job.zoom, job.minNativeZoom);
          if (activeMarinaCode) document.dispatchEvent(new CustomEvent("hydrotools:tileset-ready", { detail: { marina: activeMarinaCode } }));
          status = "Tiles generated and loaded as a map layer.";
          tiffExportProgress = 100;
          render();
        } else if (job.status === "failed") {
          stopTiffExportPoll();
          tiffExportBusy = false;
          tileExportJob = null; tileExportKind = "";
          taskIdle();
          status = `Tile generation failed: ${job.error || job.message}`;
          tiffExportProgress = 0;
          render();
        }
      } catch { /* poll errors are transient, keep trying */ }
    }, 1500);
  }

  async function generateAndLoadHiresTiles() {
    pullStateFromForm();
    const epsgCode = String(state.epsgCode || "").trim();
    if (!epsgCode) { status = "Enter the source EPSG code (e.g. 2227) first."; render(); return; }

    // Use all source files from the active Potree project; fall back to single filePath
    const allSourcePaths = (potreeJob?.sourcePaths?.length)
      ? potreeJob.sourcePaths.map(String).filter(Boolean)
      : [String(state.filePath || "").trim()].filter(Boolean);
    if (!allSourcePaths.length) { status = "Open a LAS/LAZ dataset first."; render(); return; }

    stopTiffExportPoll();
    tiffExportBusy = true;
    tiffExportProgress = 0;
    tiffExportMessage = "Starting high-res tile generation...";
    render();

    const style = currentPotreeStyle();
    let jobId = null;

    try {
      const start = await requestJson("/api/point-cloud/generate-tiles-hires", {
        method: "POST",
        body: {
          sourcePaths: allSourcePaths,
          sourceEpsg: epsgCode,
          resolution: state.hiresResolution,
          minZoom: state.hiresMinZoom,
          maxZoom: state.hiresMaxZoom,
          attribute: style.attribute,
          colorRamp: potreeGradientToColorRamp(style.gradient),
          stretchMin: Number.isFinite(style.elevationMin) ? style.elevationMin : undefined,
          stretchMax: Number.isFinite(style.elevationMax) ? style.elevationMax : undefined,
          zClipEnabled: style.zClipEnabled,
          zClipMin: style.zClipMin,
          zClipMax: style.zClipMax,
          datasetName: String(state.potreeDatasetName || "").trim() || allSourcePaths[0].split("/").pop(),
          marina: activeMarinaCode || undefined,
        },
      });
      if (!start?.ok || !start?.jobId) throw new Error(start?.error || "failed to start hires tile job");
      jobId = start.jobId;
      lastHiresJobId = jobId;
      app.log("tiles.hires_started", { marina: activeMarinaCode, resolution: state.hiresResolution });
    } catch (error) {
      tiffExportBusy = false;
      status = `High-res tile generation failed: ${String(error?.message || error)}`;
      render();
      return;
    }

    taskStatus("Hi-res tile generation starting…", 0);
    tiffExportPollTimer = setInterval(async () => {
      try {
        const res = await requestJson(`/api/point-cloud/generate-tiles/jobs/${jobId}`);
        const job = res?.job;
        if (!job) return;
        tiffExportProgress = Number(job.progressPct) || 0;
        tiffExportMessage = String(job.message || "");
        tileExportJob = job;
        tileExportKind = "hires";
        taskStatus(job.message || job.stage || "Generating hi-res tiles…", tiffExportProgress);
        render();

        if (job.status === "completed") {
          stopTiffExportPoll();
          tiffExportBusy = false;
          tileExportJob = null; tileExportKind = "";
          taskIdle();
          if (job.tileUrl) addTileLayer(jobId, job.tileUrl, job.wgs84Bounds, job.zoom, job.minNativeZoom);
          if (activeMarinaCode) document.dispatchEvent(new CustomEvent("hydrotools:tileset-ready", { detail: { marina: activeMarinaCode } }));
          status = "High-res tiles generated and loaded as a map layer.";
          tiffExportProgress = 100;
          render();
        } else if (job.status === "failed") {
          stopTiffExportPoll();
          tiffExportBusy = false;
          tileExportJob = null; tileExportKind = "";
          taskIdle();
          status = `High-res tile generation failed: ${job.error || job.message}`;
          tiffExportProgress = 0;
          render();
        }
      } catch { /* poll errors are transient, keep trying */ }
    }, 750);
  }

  function oneDecimal(value, fallback = 0) {
    const n = Number(value);
    const v = Number.isFinite(n) ? n : Number(fallback);
    return (Math.round(v * 10) / 10).toFixed(1);
  }

  function buildElevationHistogramSvg(points, minZ, maxZ, bins = 48, selMin = null, selMax = null) {
    const zs = [];
    for (const p of (points || [])) {
      const z = Number(p?.z);
      if (Number.isFinite(z)) zs.push(z);
    }
    if (!zs.length) return `<div class="small" style="opacity:.75;">No preview elevations for histogram.</div>`;
    const lo = Number.isFinite(minZ) ? minZ : Math.min(...zs);
    const hi = Number.isFinite(maxZ) ? maxZ : Math.max(...zs);
    const span = Math.max(1e-9, hi - lo);
    const counts = Array.from({ length: bins }, () => 0);
    for (const z of zs) {
      if (z < lo || z > hi) continue;
      let i = Math.floor(((z - lo) / span) * bins);
      i = Math.max(0, Math.min(bins - 1, i));
      counts[i] += 1;
    }
    const maxCount = Math.max(...counts, 1);
    const width = 540;
    const height = 120;
    const pad = 10;
    const innerW = width - pad * 2;
    const innerH = height - pad * 2;
    const barW = innerW / bins;
    const bars = counts.map((c, i) => {
      const h = Math.max(1, (c / maxCount) * innerH);
      const x = pad + (i * barW);
      const y = pad + (innerH - h);
      return `<rect x="${x.toFixed(2)}" y="${y.toFixed(2)}" width="${Math.max(1, barW - 0.6).toFixed(2)}" height="${h.toFixed(2)}" rx="1.5" fill="url(#pcwHistGrad)" />`;
    }).join("");
    const sMin = Number.isFinite(Number(selMin)) ? Number(selMin) : lo;
    const sMax = Number.isFinite(Number(selMax)) ? Number(selMax) : hi;
    const clampedMin = Math.max(lo, Math.min(hi, Math.min(sMin, sMax)));
    const clampedMax = Math.max(lo, Math.min(hi, Math.max(sMin, sMax)));
    const minX = pad + (((clampedMin - lo) / span) * innerW);
    const maxX = pad + (((clampedMax - lo) / span) * innerW);
    return `
      <svg viewBox="0 0 ${width} ${height}" width="100%" height="${height}" preserveAspectRatio="none" style="display:block; border:1px solid rgba(148,163,184,.22); border-radius:8px; background:linear-gradient(180deg, rgba(2,6,23,.55), rgba(2,6,23,.75));">
        <defs>
          <linearGradient id="pcwHistGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stop-color="#22d3ee"/>
            <stop offset="100%" stop-color="#0ea5e9"/>
          </linearGradient>
        </defs>
        <rect x="${minX.toFixed(2)}" y="${pad}" width="${Math.max(1, maxX - minX).toFixed(2)}" height="${innerH}" fill="rgba(245,158,11,.16)" />
        ${bars}
        <line x1="${minX.toFixed(2)}" y1="${pad}" x2="${minX.toFixed(2)}" y2="${(pad + innerH).toFixed(2)}" stroke="#f59e0b" stroke-width="2" />
        <line x1="${maxX.toFixed(2)}" y1="${pad}" x2="${maxX.toFixed(2)}" y2="${(pad + innerH).toFixed(2)}" stroke="#f97316" stroke-width="2" />
      </svg>
    `;
  }

  function setElevationRangeInUI(nextMin, nextMax) {
    const lo = Math.min(Number(nextMin), Number(nextMax));
    const hi = Math.max(Number(nextMin), Number(nextMax));
    const loTxt = oneDecimal(lo, lo);
    const hiTxt = oneDecimal(hi, hi);
    state.potreeElevationMin = Number(loTxt);
    state.potreeElevationMax = Number(hiTxt);
    persist();
  }

  function nudgeClipValue(kind, delta) {
    const { minZ: zMin, maxZ: zMax } = getActiveZBounds();
    const curMin = Number(state.potreeZClipMin ?? zMin);
    const curMax = Number(state.potreeZClipMax ?? zMax);
    if (kind === "max") {
      const next = Math.max(curMin, Math.min(zMax, curMax + delta));
      state.potreeZClipMax = Math.round(next * 10) / 10;
    } else {
      const next = Math.min(curMax, Math.max(zMin, curMin + delta));
      state.potreeZClipMin = Math.round(next * 10) / 10;
    }
  }

  // Potree's ClassificationScheme.DEFAULT colors (normalized to hex for the color picker)
  const ASPRS_CLASSES = [
    { id: 0,  name: "Created, Never Classified", color: "#808080" },
    { id: 1,  name: "Unclassified",              color: "#808080" },
    { id: 2,  name: "Ground",                    color: "#333333" },
    { id: 3,  name: "Low Vegetation",            color: "#008000" },
    { id: 4,  name: "Medium Vegetation",         color: "#00cc00" },
    { id: 5,  name: "High Vegetation",           color: "#00ff00" },
    { id: 6,  name: "Building",                  color: "#ffaa00" },
    { id: 7,  name: "Low Point (Noise)",         color: "#ff00ff" },
    { id: 8,  name: "Model Key-Point",           color: "#ff0000" },
    { id: 9,  name: "Water",                     color: "#0000ff" },
    { id: 10, name: "Rail",                      color: "#404040" },
    { id: 11, name: "Road Surface",              color: "#404040" },
    { id: 12, name: "Overlap / Reserved",        color: "#ff0000" },
    { id: 13, name: "Wire Guard",                color: "#ff6600" },
    { id: 14, name: "Wire Conductor",            color: "#ff9900" },
    { id: 15, name: "Transmission Tower",        color: "#cc6600" },
    { id: 16, name: "Wire-Structure Connector",  color: "#996600" },
    { id: 17, name: "Bridge Deck",               color: "#808080" },
    { id: 18, name: "High Noise",                color: "#ff0000" },
  ];

  function asprsClassName(code) {
    return ASPRS_CLASSES.find((c) => c.id === Number(code))?.name || "Unknown";
  }

  function classDefaultColor(id) {
    return ASPRS_CLASSES.find((c) => c.id === Number(id))?.color || "#808080";
  }

  function buildTilePyramidHtml(job, kind) {
    const levels = Array.isArray(job?.zoomLevels) ? job.zoomLevels : [];
    if (!levels.length) {
      return `<div style="margin-top:5px; height:6px; border-radius:3px; background:rgba(255,255,255,.1); overflow:hidden;">
        <div style="height:100%; width:${Math.max(2, tiffExportProgress)}%; background:linear-gradient(90deg,#22d3ee,#3b82f6); border-radius:3px;"></div>
      </div>
      <div class="small" style="margin-top:2px; opacity:.55; font-size:10px;">${esc(tiffExportMessage)}</div>`;
    }

    const sorted = [...levels].sort((a, b) => a.zl - b.zl);
    const n = sorted.length;
    const pct = Number(tiffExportProgress) || 0;

    function levelFill(lvl, i) {
      if (kind === "tiles" || (kind === "hires" && lvl.total > 0)) {
        return lvl.total > 0 ? Math.min(100, (lvl.done / lvl.total) * 100) : 0;
      }
      // hires before tile totals known: simulate from overall progressPct
      if (pct < 70) return (pct / 70) * 28;  // all bars fill together up to 28%
      const tilePhase = Math.min(1, (pct - 70) / 26);  // 0-1 over 70-96%
      const segStart = i / n;
      const segEnd = (i + 1) / n;
      if (tilePhase >= segEnd) return 100;
      if (tilePhase <= segStart) return 28;
      return 28 + ((tilePhase - segStart) * n) * 72;
    }

    const rows = sorted.map((lvl, i) => {
      const barW = n > 1 ? (20 + (i / (n - 1)) * 80) : 100;
      const fill = levelFill(lvl, i).toFixed(1);
      const hue = Math.round(185 + (i / Math.max(1, n - 1)) * 30);
      const lit = Math.round(65 - (i / Math.max(1, n - 1)) * 20);
      const color = `hsl(${hue},72%,${lit}%)`;
      const track = `hsla(${hue},30%,35%,.2)`;
      const label = lvl.total > 0 ? `z${lvl.zl} ${lvl.done}/${lvl.total}` : `z${lvl.zl}`;
      return `<div style="width:${barW.toFixed(1)}%; margin:0 auto 3px; display:flex; align-items:center; gap:5px;">
        <div style="flex:1; height:7px; border-radius:3.5px; background:${track}; overflow:hidden;">
          <div style="height:100%; width:${fill}%; border-radius:3.5px; background:${color};"></div>
        </div>
        <span style="font-size:9px; opacity:.5; white-space:nowrap; min-width:32px;">${esc(label)}</span>
      </div>`;
    }).join("");

    return `<div style="margin-top:8px; padding:8px 10px 6px; background:rgba(2,6,23,.45); border:1px solid rgba(148,163,184,.15); border-radius:8px;">
      <div style="font-size:9px; letter-spacing:.06em; opacity:.4; text-align:center; margin-bottom:6px; text-transform:uppercase;">Tile Pyramid</div>
      ${rows}
      <div style="font-size:10px; opacity:.5; text-align:center; margin-top:5px;">${esc(tiffExportMessage)}</div>
    </div>`;
  }

  function render() {
    if (!containerEl) return;
    const { minZ: zMin, maxZ: zMax } = getActiveZBounds();
    const elevMin = Number.isFinite(Number(state.potreeElevationMin)) ? Number(state.potreeElevationMin) : zMin;
    const elevMax = Number.isFinite(Number(state.potreeElevationMax)) ? Number(state.potreeElevationMax) : zMax;
    const zClipMin = Number.isFinite(Number(state.potreeZClipMin)) ? Number(state.potreeZClipMin) : zMin;
    const zClipMax = Number.isFinite(Number(state.potreeZClipMax)) ? Number(state.potreeZClipMax) : zMax;
    const histDomainMin = state.potreeZClipEnabled ? Math.min(zClipMin, zClipMax) : zMin;
    const histDomainMax = state.potreeZClipEnabled ? Math.max(zClipMin, zClipMax) : zMax;
    const histSvg = buildElevationHistogramSvg([], histDomainMin, histDomainMax, 104, elevMin, elevMax);
    const isElevation = String(state.potreeAttribute) === "elevation";
    const isIntensity = String(state.potreeAttribute) === "intensity";
    const showRampStyling = isElevation || isIntensity;
    const rampOptions = rampsForAttribute(state.potreeAttribute);
    const selectedRamp = rampOptions.includes(String(state.potreeGradient || "").toUpperCase())
      ? String(state.potreeGradient || "").toUpperCase()
      : rampOptions[0];
    const projectFiles = Array.isArray(state.projectFiles) ? state.projectFiles : [];
    const potreeProjects = Array.isArray(state.potreeProjects) ? [...state.potreeProjects].reverse() : [];
    const hasName = String(state.potreeDatasetName || "").trim().length > 0;
    const selectedFiles = projectFiles.filter((f) => f.selected !== false);
    const generateCount = selectedFiles.length || projectFiles.length;
    const hasFiles = projectFiles.length > 0;
    const hasActiveProject = !!potreeJob?.id;
    containerEl.innerHTML = `
      <div id="pcw-root" class="card" style="padding:10px;">
        <style>
          #pcw-root * { transition: none !important; }
          #pcw-root *:focus { outline: none !important; box-shadow: none !important; }
          #pcw-root .toolset-group,
          #pcw-root .toolset-group:hover,
          #pcw-root .toolset-group:focus-within,
          #pcw-root .toolset-content,
          #pcw-root .card {
            box-shadow: none !important;
            border-color: rgba(148,163,184,.22) !important;
          }
        </style>
        <div class="lm-title">Point Cloud Workspace</div>
        <div class="small" style="margin-top:6px; color:#64748b;">LAS/LAZ files load automatically from the Digital Asset Manager.</div>

        <!-- LAS Files -->
        <details class="toolset-group" open style="margin-top:10px;">
          <summary>LAS Files${hasFiles ? ` (${projectFiles.length})` : ""}</summary>
          <div class="toolset-content">
            <div style="margin-top:6px; max-height:180px; overflow-y:auto; border:1px solid rgba(148,163,184,.22); border-radius:8px; padding:6px;">
              ${projectFiles.length ? projectFiles.map((item) => renderLasFileItem(item)).join("") : `<div class="small" style="opacity:.75;">No LAS files loaded. Select a marina in the Digital Asset Manager with LAS files.</div>`}
            </div>
            <div style="margin-top:10px;">
              <label class="small" style="display:block; margin-bottom:4px;">Dataset Name <span style="color:#ef4444; font-size:10px;">required</span></label>
              <input id="pcw-generate-name" type="text" value="${esc(state.potreeDatasetName)}" placeholder="e.g. LittleCal MBES 2026" style="width:100%;" />
              <button id="pcw-potree-generate" class="btn primary" style="margin-top:6px; width:100%;"${potreeBusy || !hasFiles || !hasName ? " disabled" : ""}>
                ${potreeBusy ? "Generating Potree…" : !hasFiles ? "No LAS files loaded" : !hasName ? "Enter a dataset name above" : `Generate Potree (${generateCount} file${generateCount > 1 ? "s" : ""})`}
              </button>
            </div>
            ${status ? `<div class="small" style="margin-top:8px; color:#cbd5e1;">${esc(status)}</div>` : ""}
          </div>
        </details>

        <!-- Potree Projects -->
        <details class="toolset-group" open style="margin-top:10px;">
          <summary>Potree Projects${potreeProjects.length ? ` (${potreeProjects.length})` : ""}</summary>
          <div class="toolset-content" style="margin-top:6px;">
            ${potreeProjects.length ? potreeProjects.map((proj) => renderPotreeProjectItem(proj)).join("") : `<div class="small" style="opacity:.75;">No projects yet. Add a dataset name and click Generate Potree.</div>`}
          </div>
        </details>

        <!-- Viewer Controls -->
        <details class="toolset-group"${hasActiveProject || tiffExportBusy ? " open" : ""} style="margin-top:10px;">
          <summary>Viewer Controls${hasActiveProject ? ` — ${esc(potreeJob?.datasetName || "")}` : ""}</summary>
          <div class="toolset-content">
          ${!hasActiveProject ? `<div class="small" style="opacity:.75; margin-top:6px;">Open a Potree project above to enable viewer controls.</div>` : `
            <div style="margin-top:10px; padding:8px; border:1px solid rgba(148,163,184,.22); border-radius:8px;">
              <div class="small" style="font-weight:600;">High-Res Tile Pyramid</div>
              <div class="small" style="margin-top:4px; opacity:.7;">Single PDAL run → full GeoTIFF → gdalwarp → gdal2tiles.py. Rasterizes all data in the full elevation range regardless of viewer display clip.</div>
              <div style="margin-top:6px; height:10px; border-radius:4px; background:${gradientCss(state.potreeGradient)};"></div>
              <div class="small" style="margin-top:3px; opacity:.6;">${esc(state.potreeAttribute || "elevation")} · ${esc(state.potreeGradient)} · ${oneDecimal(state.potreeElevationMin, 0)}–${oneDecimal(state.potreeElevationMax, 100)}</div>
              <div style="margin-top:6px; display:grid; grid-template-columns:1fr 1fr 1fr; gap:6px;">
                <div>
                  <label class="small" style="display:block; margin-bottom:3px;">Resolution</label>
                  <input id="pcw-hires-resolution" type="number" min="0.01" max="10" step="0.01" value="${esc(String(state.hiresResolution || 0.1))}" style="width:100%; font-size:12px;" title="Native CRS units (e.g. 0.1 ft for EPSG:2227, 0.03 for meters)" />
                </div>
                <div>
                  <label class="small" style="display:block; margin-bottom:3px;">Min Zoom</label>
                  <select id="pcw-hires-min-zoom" style="width:100%; font-size:12px;">
                    ${[10,12,14,16].map((z) => `<option value="${z}"${state.hiresMinZoom === z ? " selected" : ""}>${z}</option>`).join("")}
                  </select>
                </div>
                <div>
                  <label class="small" style="display:block; margin-bottom:3px;">Max Zoom</label>
                  <select id="pcw-hires-max-zoom" style="width:100%; font-size:12px;">
                    ${[18,19,20,21,22,23,24].map((z) => `<option value="${z}"${state.hiresMaxZoom === z ? " selected" : ""}>${z}</option>`).join("")}
                  </select>
                </div>
              </div>
              <div class="small" style="margin-top:4px; opacity:.55; font-size:10px;">CRS from project context (${esc(state.epsgCode || "none set")}). For 0.1ft: EPSG:2227 → res 0.1 → max zoom 22. For 0.03m: any meter CRS → res 0.03 → max zoom 22.</div>
              <div style="display:flex; gap:6px; margin-top:6px;">
                <button id="pcw-generate-tiles-hires" class="btn" style="flex:1;"${busy || potreeBusy || tiffExportBusy ? " disabled" : ""}>${tiffExportBusy && tileExportKind === "hires" ? "Generating..." : "Generate High-Res Tiles"}</button>
                ${lastHiresJobId && !tiffExportBusy ? `<button id="pcw-delete-hires-job" class="btn" style="padding:0 10px; font-size:11px; background:#7f1d1d; color:#fca5a5; border-color:#991b1b;" title="Delete tile files from VM disk (${lastHiresJobId})">Delete tiles</button>` : ""}
              </div>
              ${tiffExportBusy && tileExportKind === "hires" ? buildTilePyramidHtml(tileExportJob, "hires") : ""}
              ${status && !tiffExportBusy ? `<div class="small" style="margin-top:6px; color:#fca5a5;">${esc(status)}</div>` : ""}
            </div>
            <div class="row lm-add-row" style="margin-top:6px;">
              <div class="small" style="color:#94a3b8;">Selection/clip tools temporarily disabled while we stabilize style + large-file loading.</div>
            </div>
            <div class="row lm-add-row" style="margin-top:10px; display:block;">
              <label class="small" style="display:block; margin-bottom:4px;">Point Budget</label>
              <input id="pcw-potree-budget" type="number" min="100000" max="30000000" step="100000" value="${esc(String(state.potreePointBudget || 3000000))}" style="width:100%;" />
            </div>
            <div class="row lm-add-row" style="margin-top:6px; display:block;">
              <label class="small" style="display:block; margin-bottom:4px;">Point Size</label>
              <input id="pcw-potree-size" type="number" min="0.1" max="10" step="0.1" value="${esc(String(state.potreePointSize || 0.8))}" style="width:100%;" />
            </div>
            <div class="row lm-add-row" style="margin-top:6px; display:block;">
              <label id="pcw-potree-vertical-exaggeration-label" class="small" style="display:block; margin-bottom:4px;">Vertical Exaggeration: ${esc(oneDecimal(state.potreeVerticalExaggeration, 1))}x</label>
              <input id="pcw-potree-vertical-exaggeration" type="range" min="1" max="10" step="0.1" value="${esc(oneDecimal(state.potreeVerticalExaggeration, 1))}" style="width:100%;" />
            </div>
            <div class="row lm-add-row" style="margin-top:6px; display:block;">
              <label class="small" style="display:block; margin-bottom:4px;">Point Shape</label>
              <select id="pcw-potree-shape" style="width:100%;">
                <option value="CIRCLE"${String(state.potreePointShape) === "CIRCLE" ? " selected" : ""}>Circle</option>
                <option value="SQUARE"${String(state.potreePointShape) === "SQUARE" ? " selected" : ""}>Square</option>
                <option value="PARABOLOID"${String(state.potreePointShape) === "PARABOLOID" ? " selected" : ""}>Paraboloid</option>
              </select>
            </div>
            <div class="row lm-add-row" style="margin-top:6px; display:block;">
              <label class="small" style="display:block; margin-bottom:4px;">Color By</label>
              <select id="pcw-potree-attribute" style="width:100%;">
                <option value="classification"${String(state.potreeAttribute) === "classification" ? " selected" : ""}>Classification</option>
                <option value="elevation"${String(state.potreeAttribute) === "elevation" ? " selected" : ""}>Elevation</option>
                <option value="rgba"${String(state.potreeAttribute) === "rgba" ? " selected" : ""}>RGB/RGBA</option>
                <option value="intensity"${String(state.potreeAttribute) === "intensity" ? " selected" : ""}>Intensity</option>
              </select>
              <button id="pcw-potree-apply-style" class="btn" style="margin-top:6px;"${!potreeJob?.viewerUrl ? " disabled" : ""}>Apply Live</button>
              <button id="pcw-potree-axis-zoom" class="btn" style="margin-top:6px;"${!potreeJob?.viewerUrl ? " disabled" : ""}>Zoom To Axis</button>
            </div>
            <div class="row lm-add-row" style="margin-top:6px; display:block;">
              <label class="small" style="display:block; margin-bottom:4px;">Axis Overlay</label>
              <label class="small" style="display:inline-flex; align-items:center; gap:8px;">
                <input id="pcw-potree-axis-enabled" type="checkbox" ${state.potreeAxisEnabled !== false ? "checked" : ""} />
                Show XYZ Axes
              </label>
              <label class="small" style="display:inline-flex; align-items:center; gap:8px; margin-left:12px;">
                <input id="pcw-potree-axis-ticks-enabled" type="checkbox" ${state.potreeAxisTicksEnabled !== false ? "checked" : ""} />
                Labels/Ticks
              </label>
            </div>
            <div style="margin-top:10px; padding:8px; border:1px solid rgba(148,163,184,.22); border-radius:8px;">
              <div class="small" style="font-weight:600;">Eye-Dome-Lighting</div>
              <div class="row lm-add-row" style="margin-top:6px; display:block;">
                <label class="small" style="display:block; margin-bottom:4px;">Enable</label>
                <input id="pcw-potree-edl-enabled" type="checkbox" ${state.potreeEdlEnabled ? "checked" : ""} />
              </div>
              <div class="row lm-add-row" style="margin-top:6px; display:block;">
                <label class="small" style="display:block; margin-bottom:4px;">Radius: ${esc(oneDecimal(state.potreeEdlRadius, 1))}</label>
                <input id="pcw-potree-edl-radius" type="range" min="0.1" max="4" step="0.1" value="${esc(oneDecimal(state.potreeEdlRadius, 1))}" style="flex:1;" />
              </div>
              <div class="row lm-add-row" style="margin-top:6px; display:block;">
                <label class="small" style="display:block; margin-bottom:4px;">Strength: ${esc(oneDecimal(state.potreeEdlStrength, 0.3))}</label>
                <input id="pcw-potree-edl-strength" type="range" min="0" max="5" step="0.1" value="${esc(oneDecimal(state.potreeEdlStrength, 0.3))}" style="flex:1;" />
              </div>
              <div class="row lm-add-row" style="margin-top:6px; display:block;">
                <label class="small" style="display:block; margin-bottom:4px;">Opacity: ${esc(oneDecimal(state.potreeEdlOpacity, 1))}</label>
                <input id="pcw-potree-edl-opacity" type="range" min="0" max="1" step="0.05" value="${esc(oneDecimal(state.potreeEdlOpacity, 1))}" style="flex:1;" />
              </div>
            </div>
            ${depthLayerAvailable ? `
            <div style="margin-top:10px; padding:8px; border:1px solid rgba(148,163,184,.22); border-radius:8px;">
              <div class="small" style="font-weight:600;">Bathymetric Surface</div>
              <div class="row lm-add-row" style="margin-top:6px; display:block;">
                <label class="small" style="display:inline-flex; align-items:center; gap:8px; cursor:pointer;">
                  <input id="pcw-potree-surface-enabled" type="checkbox" ${state.potreeSurfaceEnabled ? "checked" : ""} />
                  Show Depth Surface
                </label>
              </div>
              ${state.potreeSurfaceEnabled ? `<div class="row lm-add-row" style="margin-top:6px; display:block;">
                <label id="pcw-potree-surface-opacity-label" class="small" style="display:block; margin-bottom:4px;">Opacity: ${esc(oneDecimal(state.potreeSurfaceOpacity, 0.7))}</label>
                <input id="pcw-potree-surface-opacity" type="range" min="0.1" max="1" step="0.05" value="${esc(oneDecimal(state.potreeSurfaceOpacity, 0.7))}" style="flex:1;" />
              </div>
              <div class="small" style="margin-top:6px; opacity:.8; ${surfaceStatus === "loading" ? "color:#60a5fa;" : surfaceStatus === "error" ? "color:#f87171;" : surfaceStatus === "ready" ? "color:#4ade80;" : ""}">
                ${surfaceStatus === "loading" ? "Loading surface…" : surfaceStatus === "error" ? "Error: " + esc(surfaceStatusMsg || "surface failed") : surfaceStatus === "ready" ? "Surface ready" : ""}
              </div>` : ""}
            </div>` : ""}
            <div id="pcw-potree-elev-styling" style="margin-top:10px; padding:8px; border:1px solid rgba(148,163,184,.22); border-radius:8px; display:${showRampStyling ? "block" : "none"};">
              <div class="small" style="font-weight:600;">Value Styling</div>
              <div class="row lm-add-row" style="margin-top:6px; display:block;">
                <label class="small" style="display:block; margin-bottom:4px;">Color Ramp</label>
                <select id="pcw-potree-gradient" style="width:100%;">
                  ${rampOptions.map((r) => `<option value="${r}"${selectedRamp === r ? " selected" : ""}>${esc(r.replaceAll("_", "-").toLowerCase().replace(/^./, (m) => m.toUpperCase()))}</option>`).join("")}
                </select>
              </div>
              <div class="row lm-add-row" style="margin-top:8px; display:${showRampStyling ? "block" : "none"};">
                <label class="small" style="display:flex; align-items:center; gap:8px;">
                  <input id="pcw-potree-bounds-manual-enabled" type="checkbox" ${state.potreeBoundsManualEnabled ? "checked" : ""} />
                  Use Manual Bounds Override
                </label>
                <div style="display:grid; grid-template-columns:1fr 1fr; gap:8px; margin-top:6px;">
                  <input id="pcw-potree-bounds-manual-min" type="number" step="0.1" value="${esc(oneDecimal(state.potreeBoundsManualMin, zMin))}" placeholder="Min Z" />
                  <input id="pcw-potree-bounds-manual-max" type="number" step="0.1" value="${esc(oneDecimal(state.potreeBoundsManualMax, zMax))}" placeholder="Max Z" />
                </div>
                <div class="small" style="margin-top:4px; opacity:.75;">Turn this on if auto bounds are wrong; all elevation controls will use these values.</div>
                <button id="pcw-potree-bounds-autodetect" class="btn" type="button" style="margin-top:6px;">Auto-Detect Bounds</button>
              </div>
              <div class="row lm-add-row" style="margin-top:6px; display:${showRampStyling ? "block" : "none"};">
                <label class="small" style="display:block; margin-bottom:4px;">${isIntensity ? "Intensity Range" : "Elevation Range"}</label>
                <div style="display:grid; grid-template-columns:1fr 1fr; gap:8px;">
                  <div class="small">Min: ${esc(oneDecimal(elevMin, zMin))}</div>
                  <div class="small" style="text-align:right;">Max: ${esc(oneDecimal(elevMax, zMax))}</div>
                </div>
              </div>
              <div class="row lm-add-row" style="margin-top:8px; display:${showRampStyling ? "block" : "none"};">
                <label class="small" style="display:block; margin-bottom:4px;">Elevation Histogram</label>
                <div id="pcw-potree-hist-wrap" style="width:100%;" data-zmin="${esc(String(histDomainMin))}" data-zmax="${esc(String(histDomainMax))}">
                  <div id="pcw-potree-elev-hist">${histSvg}</div>
                  <div class="small" style="margin-top:4px; opacity:.75;">Drag orange lines to set min/max range.</div>
                </div>
              </div>
              <div class="row lm-add-row" style="margin-top:8px; display:${showRampStyling ? "block" : "none"};">
                <label class="small" style="display:block; margin-bottom:4px;">Color Ramp Tuner</label>
                <div
                  id="pcw-potree-ramp-tuner"
                  data-zmin="${esc(String(zMin))}"
                  data-zmax="${esc(String(zMax))}"
                  style="position:relative; height:18px; border-radius:999px; border:1px solid rgba(148,163,184,.35); background:${esc(gradientCss(selectedRamp))}; cursor:pointer;"
                >
                  <div
                    style="position:absolute; left:${esc(String(((Math.min(elevMin, elevMax) - zMin) / Math.max(1e-9, zMax - zMin)) * 100))}%; width:${esc(String(((Math.max(elevMin, elevMax) - Math.min(elevMin, elevMax)) / Math.max(1e-9, zMax - zMin)) * 100))}%; top:0; bottom:0; background:rgba(2,6,23,.35);"
                  ></div>
                  <div data-handle="min" style="position:absolute; left:calc(${esc(String(((Math.min(elevMin, elevMax) - zMin) / Math.max(1e-9, zMax - zMin)) * 100))}% - 7px); top:50%; transform:translateY(-50%); width:14px; height:14px; border-radius:999px; border:2px solid #fb923c; background:#0f172a;"></div>
                  <div data-handle="max" style="position:absolute; left:calc(${esc(String(((Math.max(elevMin, elevMax) - zMin) / Math.max(1e-9, zMax - zMin)) * 100))}% - 7px); top:50%; transform:translateY(-50%); width:14px; height:14px; border-radius:999px; border:2px solid #f59e0b; background:#0f172a;"></div>
                </div>
              </div>
              ${ENABLE_ABSOLUTE_Z_CLIP ? `
              <div class="row lm-add-row" style="margin-top:8px; display:block;">
                <label class="small" style="display:block; margin-bottom:4px;">Hide Above Z</label>
                <div style="display:flex; align-items:center; gap:8px;">
                  <input id="pcw-potree-zclip-enabled" type="checkbox" ${state.potreeZClipEnabled ? "checked" : ""} />
                  <input id="pcw-potree-zclip-max-slider" type="range" min="${esc(String(zMin))}" max="${esc(String(zMax))}" step="0.05" value="${esc(oneDecimal(zClipMax, zMax))}" style="flex:1;" />
                  <button id="pcw-potree-zclip-max-down" class="btn" type="button" style="padding:2px 8px; min-width:28px;">-</button>
                  <button id="pcw-potree-zclip-max-up" class="btn" type="button" style="padding:2px 8px; min-width:28px;">+</button>
                  <input id="pcw-potree-zclip-max-readout" type="text" value="${esc(oneDecimal(zClipMax, zMax))}" style="width:72px;" readonly />
                </div>
              </div>
              <div class="row lm-add-row" style="margin-top:6px; display:block;">
                <label class="small" style="display:block; margin-bottom:4px;">Hide Below Z</label>
                <div style="display:flex; align-items:center; gap:8px;">
                  <input id="pcw-potree-zclip-min-slider" type="range" min="${esc(String(zMin))}" max="${esc(String(zMax))}" step="0.05" value="${esc(oneDecimal(zClipMin, zMin))}" style="flex:1;" />
                  <button id="pcw-potree-zclip-min-down" class="btn" type="button" style="padding:2px 8px; min-width:28px;">-</button>
                  <button id="pcw-potree-zclip-min-up" class="btn" type="button" style="padding:2px 8px; min-width:28px;">+</button>
                  <input id="pcw-potree-zclip-min-readout" type="text" value="${esc(oneDecimal(zClipMin, zMin))}" style="width:72px;" readonly />
                </div>
              </div>
              ` : ""}
            </div>
            <div style="margin-top:10px; padding:8px; border:1px solid rgba(148,163,184,.22); border-radius:8px;">
              <div class="small" style="font-weight:600; margin-bottom:4px;">Classification Styling</div>
              ${ASPRS_CLASSES.map(({ id, name }) => {
                const color = state.potreeClassColors?.[id] ?? classDefaultColor(id);
                const vis = state.potreeClassVisibility?.[id] !== false;
                return `<div class="row lm-add-row" style="margin-top:5px; align-items:center;">
                  <input id="pcw-class-vis-${id}" type="checkbox" ${vis ? "checked" : ""} style="flex-shrink:0;" />
                  <input id="pcw-class-color-${id}" type="color" value="${esc(color)}" style="width:36px; height:22px; padding:1px; flex-shrink:0; margin-left:6px; cursor:pointer;" />
                  <label class="small" style="margin-left:6px; cursor:pointer;" for="pcw-class-vis-${id}">${id} — ${esc(name)}</label>
                </div>`;
              }).join("")}
            </div>
            <div class="small" style="margin-top:8px; color:${potreeJob?.error ? "#fca5a5" : "#94a3b8"};">
              ${esc(potreeJob?.error || potreeJob?.message || "")}
            </div>
          `}
          </div>
        </details>
      </div>
    `;
  }

  function refreshHistogramOnly() {
    if (!containerEl) return;
    const fullBounds = potreeJob?.bounds || dataset?.fullBounds || dataset?.bounds || null;
    const points = Array.isArray(dataset?.preview?.points) ? dataset.preview.points : [];
    const { minZ: zMin, maxZ: zMax } = getActiveZBounds();
    const elevMin = Number.isFinite(Number(state.potreeElevationMin)) ? Number(state.potreeElevationMin) : zMin;
    const elevMax = Number.isFinite(Number(state.potreeElevationMax)) ? Number(state.potreeElevationMax) : zMax;
    const zClipMin = Number.isFinite(Number(state.potreeZClipMin)) ? Number(state.potreeZClipMin) : zMin;
    const zClipMax = Number.isFinite(Number(state.potreeZClipMax)) ? Number(state.potreeZClipMax) : zMax;
    const histDomainMin = state.potreeZClipEnabled ? Math.min(zClipMin, zClipMax) : zMin;
    const histDomainMax = state.potreeZClipEnabled ? Math.max(zClipMin, zClipMax) : zMax;
    const histWrap = containerEl.querySelector("#pcw-potree-hist-wrap");
    const histHost = containerEl.querySelector("#pcw-potree-elev-hist");
    if (!histWrap || !histHost) return;
    histWrap.setAttribute("data-zmin", String(histDomainMin));
    histWrap.setAttribute("data-zmax", String(histDomainMax));
    histHost.innerHTML = buildElevationHistogramSvg(points, histDomainMin, histDomainMax, 104, elevMin, elevMax);
  }

  async function renderPreview() {
    const points = Array.isArray(dataset?.preview?.points) ? dataset.preview.points : [];
    if (!containerEl) return;
    const host = containerEl.querySelector("#pcw-preview");
    if (!host) return;
    if (preview.destroy) {
      preview.destroy();
      preview.destroy = null;
      preview.setPaused = null;
    }
    if (!points.length) {
      host.innerHTML = `<div class="small" style="position:absolute; inset:0; display:flex; align-items:center; justify-content:center; color:#94a3b8;">No preview points available.</div>`;
      return;
    }
    try {
      const libs = await THREE_LIBS.load();
      const previewHandle = mountThreePreview(host, points, libs);
      preview.destroy = previewHandle?.destroy || null;
      preview.setPaused = previewHandle?.setPaused || null;
      preview.setPaused?.(!panelOpen);
    } catch (error) {
      status = `3D preview failed: ${error?.message || error}`;
      host.innerHTML = `<div class="small" style="position:absolute; inset:0; display:flex; align-items:center; justify-content:center; color:#fca5a5; text-align:center; padding:12px;">${esc(status)}</div>`;
      render();
    }
  }

  async function refreshStatus() {
    try {
      const payload = await requestJson("/api/point-cloud/status");
      const pdalReady = payload?.tools?.pdal?.installed ? `PDAL ready (${payload.tools.pdal.version || "installed"})` : "PDAL not ready";
      status = `${pdalReady}.`;
    } catch (error) {
      status = error?.message || "Could not read point cloud tool status.";
    } finally {
      render();
    }
  }

  function stopPotreePoll() {
    if (potreePollTimer) {
      clearInterval(potreePollTimer);
      potreePollTimer = null;
    }
  }

  function stopProjectJobsPoll() {
    if (projectJobsPollTimer) {
      clearInterval(projectJobsPollTimer);
      projectJobsPollTimer = null;
    }
  }

  function startProjectJobsPoll() {
    stopProjectJobsPoll();
    projectJobsPollTimer = setInterval(() => {
      refreshProjectFileJobStatuses();
    }, 1500);
  }

  async function refreshPotreeJob() {
    if (!potreeJob?.id) return;
    try {
      const payload = await requestJson(`/api/point-cloud/potree/jobs/${encodeURIComponent(potreeJob.id)}`);
      if (payload?.job) potreeJob = payload.job;
      // Keep potreeProjects list in sync with the live job
      if (potreeJob?.id) {
        const jId = potreeJob.id;
        state.potreeProjects = (state.potreeProjects || []).map((p) =>
          p.jobId === jId
            ? { ...p, status: String(potreeJob.status || p.status), progressPct: Number(potreeJob.progressPct ?? p.progressPct), viewerUrl: String(potreeJob.viewerUrl || p.viewerUrl || ""), message: String(potreeJob.message || p.message || "") }
            : p
        );
        savePotreeProjects();
      }
      reconcileRangesToCurrentBounds();
      await hydratePotreeBoundsFromFilesIfMissing();
      const jobStatus = String(potreeJob?.status || "").toLowerCase();
      // Emit task status for sys-bar
      if (["completed", "failed", "canceled"].includes(jobStatus)) {
        taskIdle();
      } else {
        taskStatus(
          String(potreeJob?.message || jobStatus),
          Number.isFinite(Number(potreeJob?.progressPct)) ? Number(potreeJob.progressPct) : null,
        );
      }
      if (["completed", "failed", "canceled"].includes(jobStatus)) {
        potreeBusy = false;
        stopPotreePoll();
        // Sync completed job state back into project files so "Open Viewer" works
        if (potreeJob?.id) {
          const jId = potreeJob.id;
          const jStatus = jobStatus;
          const jViewerUrl = String(potreeJob.viewerUrl || "");
          const jPct = Number(potreeJob.progressPct || 100);
          const jMsg = String(potreeJob?.message || "");
          state.projectFiles = (state.projectFiles || []).map((item) =>
            String(item?.potreeJobId || "") === jId
              ? { ...item, status: jStatus, progressPct: jPct, viewerUrl: jViewerUrl, message: jMsg }
              : item
          );
          persist();
          if (jStatus === "completed" && jViewerUrl && projectAutoOpenFirstCompleted) {
            projectAutoOpenFirstCompleted = false;
            openPotreeViewer(jViewerUrl);
          }
        }
      }
    } catch (error) {
      potreeBusy = false;
      stopPotreePoll();
      potreeJob = { ...(potreeJob || {}), error: String(error?.message || error) };
    } finally {
      render();
      await renderPreview();
    }
  }

  async function startPotreeGenerate() {
    pullStateFromForm();
    potreeBusy = true;
    potreeJob = { id: "", status: "preparing", progressPct: 5, message: "Preparing source..." };
    render();
    try {
      let sourcePath = String(state.filePath || "").trim();
      if (!sourcePath) throw new Error("Select a file in the project list or enter a File Path above.");
      persist();
      render();
      await loadDataset();
      const startRes = await requestJson("/api/point-cloud/potree/jobs", {
        method: "POST",
        body: {
          datasetName: state.potreeDatasetName || sourcePath.split(/[\\/]/).pop() || "Point Cloud",
          sourcePath,
          marina: activeMarinaCode || undefined,
        },
      });
      if (!startRes?.job?.id) throw new Error("potree_job_start_failed");
      potreeJob = startRes.job;
      state.potreeLastJobId = String(potreeJob?.id || "");
      persist();
      stopPotreePoll();
      potreePollTimer = setInterval(refreshPotreeJob, 1200);
      await refreshPotreeJob();
    } catch (error) {
      potreeBusy = false;
      potreeJob = { ...(potreeJob || {}), status: "failed", progressPct: 100, error: String(error?.message || error) };
      render();
    }
  }

  async function loadDataset() {
    pullStateFromForm();
    if (!state.filePath) {
      status = "Enter a dataset file path first.";
      render();
      return;
    }
    busy = true;
    // Prevent stale job bounds/ranges from old datasets while loading a new source.
    potreeJob = null;
    stopPotreePoll();
    status = "Opening dataset...";
    render();
    try {
      const payload = await requestJson("/api/point-cloud/open", {
        method: "POST",
        body: {
          filePath: state.filePath,
          maxPreviewPoints: state.maxPreviewPoints,
        },
      });
      dataset = payload?.dataset || null;
      reconcileRangesToCurrentBounds({ resetToFull: true });
      status = `Loaded ${dataset?.format || "dataset"} with ${formatNumber(dataset?.pointCount || 0, 0)} points.`;
      await refreshOrAttachPotreeJob();
    } catch (error) {
      status = error?.message || "Could not open point cloud dataset.";
      dataset = null;
    } finally {
      busy = false;
      render();
      await renderPreview();
    }
  }

  function onInput(event) {
    if (busy) return;
    const t = event?.target;
    const id = String(t?.id || "");
    if (id === "pcw-generate-name") {
      pullStateFromForm();
      // Update button state in-place — full render would destroy the input and lose focus
      const btn = containerEl?.querySelector("#pcw-potree-generate");
      if (btn) {
        const hasFiles = (state.projectFiles || []).length > 0;
        const selectedFiles = (state.projectFiles || []).filter((f) => f.selected !== false);
        const generateCount = selectedFiles.length || (state.projectFiles || []).length;
        const hasName = !!state.potreeDatasetName;
        btn.disabled = potreeBusy || !hasFiles || !hasName;
        btn.textContent = potreeBusy ? "Generating Potree…" : !hasFiles ? "No LAS files loaded" : !hasName ? "Enter a dataset name above" : `Generate Potree (${generateCount} file${generateCount > 1 ? "s" : ""})`;
      }
      return;
    }
    const selectCb = event.target?.closest?.("[data-project-select]");
    if (selectCb && event.type === "change") {
      const selId = String(selectCb.getAttribute("data-project-select") || "");
      state.projectFiles = (state.projectFiles || []).map((f) =>
        String(f?.id || "") === selId ? { ...f, selected: selectCb.checked } : f
      );
      persist();
      render();
      return;
    }
    if (id === "pcw-potree-gradient") {
      const track = containerEl?.querySelector("#pcw-potree-elev-track");
      const rampTrack = containerEl?.querySelector("#pcw-potree-ramp-track");
      const nextGrad = containerEl?.querySelector("#pcw-potree-gradient")?.value || state.potreeGradient;
      if (track) track.style.background = gradientCss(nextGrad);
      if (rampTrack) rampTrack.style.background = gradientCss(nextGrad);
    }
    if (id === "pcw-potree-edl-radius" || id === "pcw-potree-edl-strength" || id === "pcw-potree-edl-opacity") {
      pullStateFromForm();
      pushPotreeStyleToViewer();
      return;
    }
    if (id === "pcw-potree-surface-enabled" || id === "pcw-potree-surface-opacity") {
      pullStateFromForm();
      const opacityLabel = containerEl?.querySelector("#pcw-potree-surface-opacity-label");
      if (opacityLabel) opacityLabel.textContent = `Opacity: ${oneDecimal(state.potreeSurfaceOpacity, 0.7)}`;
      if (state.potreeSurfaceEnabled && depthLayerAvailable) {
        surfaceStatus = "loading";
        surfaceStatusMsg = "";
        render();
        pushPotreeCommandToViewer("set-surface", { marina: activeMarinaCode, opacity: state.potreeSurfaceOpacity });
      } else {
        surfaceStatus = "";
        surfaceStatusMsg = "";
        pushPotreeCommandToViewer("remove-surface", {});
        render();
      }
      return;
    }
    if (id === "pcw-potree-vertical-exaggeration") {
      pullStateFromForm();
      const label = containerEl?.querySelector("#pcw-potree-vertical-exaggeration-label");
      if (label) label.textContent = `Vertical Exaggeration: ${oneDecimal(state.potreeVerticalExaggeration, 1)}x`;
      pushPotreeStyleToViewer();
      return;
    }
    if (id === "pcw-potree-zclip-max-slider") {
      const readout = containerEl?.querySelector("#pcw-potree-zclip-max-readout");
      const v = Number(containerEl?.querySelector("#pcw-potree-zclip-max-slider")?.value || state.potreeZClipMax || 0);
      if (readout && Number.isFinite(v)) readout.value = oneDecimal(v, v);
      pullStateFromForm();
      refreshHistogramOnly();
    }
    if (id === "pcw-potree-zclip-min-slider") {
      const readout = containerEl?.querySelector("#pcw-potree-zclip-min-readout");
      const v = Number(containerEl?.querySelector("#pcw-potree-zclip-min-slider")?.value || state.potreeZClipMin || 0);
      if (readout && Number.isFinite(v)) readout.value = oneDecimal(v, v);
      pullStateFromForm();
      refreshHistogramOnly();
    }
    if (id === "pcw-potree-zclip-enabled") {
      pullStateFromForm();
      refreshHistogramOnly();
    }
    if (id === "pcw-potree-bounds-manual-enabled" || id === "pcw-potree-bounds-manual-min" || id === "pcw-potree-bounds-manual-max") {
      pullStateFromForm();
      const isTypingManualBounds = (id === "pcw-potree-bounds-manual-min" || id === "pcw-potree-bounds-manual-max") && event?.type === "input";
      if (isTypingManualBounds) return;
      if (state.potreeBoundsManualEnabled) {
        reconcileRangesToCurrentBounds({ resetToFull: true });
      } else {
        reconcileRangesToCurrentBounds();
      }
      render();
      pushPotreeStyleToViewer();
      return;
    }
    if (id === "pcw-potree-attribute") {
      const wrap = containerEl?.querySelector("#pcw-potree-elev-styling");
      const attr = String(containerEl?.querySelector("#pcw-potree-attribute")?.value || "");
      if (wrap) wrap.style.display = (attr === "elevation" || attr === "intensity") ? "block" : "none";
      state.potreeAttribute = attr || "classification";
      const allowed = rampsForAttribute(state.potreeAttribute);
      if (!allowed.includes(String(state.potreeGradient || "").toUpperCase())) {
        state.potreeGradient = allowed[0];
      }
      persist();
      render();
      pullStateFromForm();
      pushPotreeStyleToViewer();
      return;
    }
    pullStateFromForm();
    if (
      id === "pcw-potree-budget" || id === "pcw-potree-size" || id === "pcw-potree-vertical-exaggeration" || id === "pcw-potree-shape" || id === "pcw-potree-attribute" ||
      id === "pcw-potree-axis-enabled" || id === "pcw-potree-axis-ticks-enabled" ||
      id === "pcw-potree-edl-enabled" || id === "pcw-potree-edl-radius" || id === "pcw-potree-edl-strength" || id === "pcw-potree-edl-opacity" ||
      id === "pcw-potree-gradient" ||
      id === "pcw-potree-bounds-manual-enabled" || id === "pcw-potree-bounds-manual-min" || id === "pcw-potree-bounds-manual-max" ||
      id === "pcw-potree-zclip-enabled" || id === "pcw-potree-zclip-max-slider" || id === "pcw-potree-zclip-max" ||
      id === "pcw-potree-zclip-min-slider" ||
      id.startsWith("pcw-class-vis-") || id.startsWith("pcw-class-color-")
    ) {
      pushPotreeStyleToViewer();
    }
  }

  function onClick(event) {
    if (event.target?.closest?.("#pcw-potree-generate")) {
      pullStateFromForm();
      generateCombinedPotreeForProjectFiles();
      return;
    }
    const projectRemoveBtn = event.target?.closest?.("[data-project-remove]");
    if (projectRemoveBtn) {
      removeProjectFile(String(projectRemoveBtn.getAttribute("data-project-remove") || ""));
      render();
      return;
    }
    const projectInspectBtn = event.target?.closest?.("[data-project-inspect]");
    if (projectInspectBtn) {
      inspectProjectFileCrs(String(projectInspectBtn.getAttribute("data-project-inspect") || ""));
      return;
    }
    const potreeProjectOpenBtn = event.target?.closest?.("[data-potree-project-open]");
    if (potreeProjectOpenBtn) {
      const jobId = String(potreeProjectOpenBtn.getAttribute("data-potree-project-open") || "");
      const proj = (state.potreeProjects || []).find((p) => p.jobId === jobId);
      if (proj) {
        const viewerUrl = proj.viewerUrl || (jobId ? `/api/point-cloud/potree/files/${encodeURIComponent(jobId)}/viewer.html` : "");
        potreeJob = { id: jobId, status: proj.status, progressPct: proj.progressPct, viewerUrl, datasetName: proj.name, message: proj.message || "", sourcePaths: proj.sourcePaths || [] };
        state.potreeLastJobId = jobId;
        if (proj.sourcePaths?.[0]) state.filePath = String(proj.sourcePaths[0]);
        persist();
        render();
        openPotreeViewer(viewerUrl);
        hydratePotreeBoundsFromFilesIfMissing().then(() => {
          reconcileRangesToCurrentBounds(); render(); pushPotreeStyleToViewer();
          if (state.potreeSurfaceEnabled && depthLayerAvailable) {
            pushPotreeCommandToViewer("set-surface", { marina: activeMarinaCode, opacity: state.potreeSurfaceOpacity });
          }
        }).catch(() => {});
      }
      return;
    }
    const potreeProjectDeleteBtn = event.target?.closest?.("[data-potree-project-delete]");
    if (potreeProjectDeleteBtn) {
      const projId = String(potreeProjectDeleteBtn.getAttribute("data-potree-project-delete") || "");
      state.potreeProjects = (state.potreeProjects || []).filter((p) => p.id !== projId);
      savePotreeProjects();
      persist();
      render();
      return;
    }
    if (event.target?.closest?.("#pcw-potree-apply-style")) {
      pullStateFromForm();
      pushPotreeStyleToViewer();
      return;
    }
    if (event.target?.closest?.("#pcw-potree-bounds-autodetect")) {
      Promise.resolve()
        .then(async () => {
          await hydratePotreeBoundsFromFilesIfMissing();
          reconcileRangesToCurrentBounds({ resetToFull: true });
          render();
          pushPotreeStyleToViewer();
        })
        .catch(() => {});
      return;
    }
    if (event.target?.closest?.("#pcw-potree-axis-zoom")) {
      pushPotreeCommandToViewer("zoom_axis_origin");
      return;
    }
    if (event.target?.closest?.("#pcw-generate-2d-layers")) {
      generateAndLoad2DLayers();
      return;
    }
    if (event.target?.closest?.("#pcw-raster-apply-style")) {
      applyRasterStyleToGeneratedLayers();
      return;
    }
    if (event.target?.closest?.("#pcw-generate-tiles-hires")) {
      generateAndLoadHiresTiles();
      return;
    }
    if (event.target?.closest?.("#pcw-delete-hires-job")) {
      deleteTileJob("hires");
      return;
    }
    if (event.target?.closest?.("#pcw-potree-zclip-max-down")) {
      nudgeClipValue("max", -Z_NUDGE_STEP);
      pushPotreeStyleToViewer();
      render();
      return;
    }
    if (event.target?.closest?.("#pcw-potree-zclip-max-up")) {
      nudgeClipValue("max", +Z_NUDGE_STEP);
      pushPotreeStyleToViewer();
      render();
      return;
    }
    if (event.target?.closest?.("#pcw-potree-zclip-min-down")) {
      nudgeClipValue("min", -Z_NUDGE_STEP);
      pushPotreeStyleToViewer();
      render();
      return;
    }
    if (event.target?.closest?.("#pcw-potree-zclip-min-up")) {
      nudgeClipValue("min", +Z_NUDGE_STEP);
      pushPotreeStyleToViewer();
      render();
      return;
    }
    
  }

  function onPointerDown(event) {
    const id = String(event?.target?.id || "");
    const rampHost = event?.target?.closest?.("#pcw-potree-ramp-tuner");
    if (rampHost) {
      const zMin = Number(rampHost.getAttribute("data-zmin"));
      const zMax = Number(rampHost.getAttribute("data-zmax"));
      if (!Number.isFinite(zMin) || !Number.isFinite(zMax) || zMax <= zMin) return;
      const rect = rampHost.getBoundingClientRect();
      const toZ = (clientX) => {
        const t = Math.max(0, Math.min(1, (clientX - rect.left) / Math.max(1, rect.width)));
        return zMin + (t * (zMax - zMin));
      };
      let minNow = Number(state.potreeElevationMin ?? zMin);
      let maxNow = Number(state.potreeElevationMax ?? zMax);
      const explicitHandle = event?.target?.getAttribute?.("data-handle");
      const startZ = toZ(event.clientX);
      let active = explicitHandle || (Math.abs(startZ - minNow) <= Math.abs(startZ - maxNow) ? "min" : "max");
      const move = (ev) => {
        const z = toZ(ev.clientX);
        if (active === "min") {
          setElevationRangeInUI(Math.min(z, maxNow), maxNow);
          minNow = Number(state.potreeElevationMin);
        } else {
          setElevationRangeInUI(minNow, Math.max(z, minNow));
          maxNow = Number(state.potreeElevationMax);
        }
        refreshHistogramOnly();
        pushPotreeStyleToViewer();
        render();
      };
      const up = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
      event.preventDefault();
      return;
    }
    const histHost = event?.target?.closest?.("#pcw-potree-hist-wrap");
    if (histHost) {
      const zMin = Number(histHost.getAttribute("data-zmin"));
      const zMax = Number(histHost.getAttribute("data-zmax"));
      let minNow = Number(state.potreeElevationMin ?? zMin);
      let maxNow = Number(state.potreeElevationMax ?? zMax);
      const span = Math.max(1e-9, zMax - zMin);
      const targetEl = containerEl?.querySelector("#pcw-potree-elev-hist svg");
      if (!targetEl || !Number.isFinite(zMin) || !Number.isFinite(zMax)) return;
      const rect = targetEl.getBoundingClientRect();
      const toZ = (clientX) => {
        const t = Math.max(0, Math.min(1, (clientX - rect.left) / Math.max(1, rect.width)));
        return zMin + (t * span);
      };
      const startZ = toZ(event.clientX);
      let active = Math.abs(startZ - minNow) <= Math.abs(startZ - maxNow) ? "min" : "max";
      const move = (ev) => {
        const z = toZ(ev.clientX);
        if (active === "min") {
          setElevationRangeInUI(Math.min(z, maxNow), maxNow);
          minNow = Number(state.potreeElevationMin);
        } else {
          setElevationRangeInUI(minNow, Math.max(z, minNow));
          maxNow = Number(state.potreeElevationMax);
        }
        pullStateFromForm();
        render();
        pushPotreeStyleToViewer();
      };
      const up = () => {
        window.removeEventListener("pointermove", move);
        window.removeEventListener("pointerup", up);
      };
      window.addEventListener("pointermove", move);
      window.addEventListener("pointerup", up);
      event.preventDefault();
      return;
    }
    return;
  }

  function onSurfaceStatusMessage(evt) {
    if (evt?.data?.type !== "hydrotools-surface-status") return;
    surfaceStatus = String(evt.data.status || "");
    surfaceStatusMsg = String(evt.data.details || "");
    render();
  }

  function onMarinaChanged(e) {
    const marinaId = String(e.detail?.marinaId || "");
    setActiveMarina(marinaId);
    const crs = String(e.detail?.horizontalCRS || "").trim();
    if (crs) setEpsgCode(crs);
  }

  function mount(el) {
    if (containerEl) {
      containerEl.removeEventListener("input", onInput);
      containerEl.removeEventListener("change", onInput);
      containerEl.removeEventListener("click", onClick);
      containerEl.removeEventListener("pointerdown", onPointerDown);
    }
    window.addEventListener("message", onSurfaceStatusMessage);
    document.addEventListener("hydrotools:marina-changed", onMarinaChanged);
    try { app?.ui?.setDockOpen?.("right", true); } catch {}
    containerEl = el;
    render();
    containerEl.addEventListener("input", onInput);
    containerEl.addEventListener("change", onInput);
    containerEl.addEventListener("click", onClick);
    containerEl.addEventListener("pointerdown", onPointerDown);
  }

  function setPanelOpen(open) {
    panelOpen = !!open;
    preview.setPaused?.(!panelOpen);
    if (panelOpen) {
      if (containerEl) render();
      if (potreeJob?.id && !potreePollTimer) refreshPotreeJob();
    }
  }

  function destroy() {
    taskIdle();
    if (preview.destroy) {
      preview.destroy();
      preview.destroy = null;
      preview.setPaused = null;
    }
    stopPotreePoll();
    stopProjectJobsPoll();
    window.removeEventListener("message", onSurfaceStatusMessage);
    document.removeEventListener("hydrotools:marina-changed", onMarinaChanged);
    if (!containerEl) return;
    containerEl.removeEventListener("input", onInput);
    containerEl.removeEventListener("change", onInput);
    containerEl.removeEventListener("click", onClick);
    containerEl.removeEventListener("pointerdown", onPointerDown);
    containerEl = null;
  }

  function snapshot() {
    return {
      version: 1,
      state: { ...state },
      potreeJobId: String(potreeJob?.id || ""),
    };
  }

  function restore(data) {
    const next = data?.state;
    if (!next || typeof next !== "object") return;
    state = {
      ...state,
      ...next,
      filePath: String(next.filePath || state.filePath || ""),
      maxPreviewPoints: Math.max(1000, Math.min(50000, Number(next.maxPreviewPoints || state.maxPreviewPoints || 12000) || 12000)),
      potreeDatasetName: String(next.potreeDatasetName || state.potreeDatasetName || ""),
      projectFiles: Array.isArray(next.projectFiles) ? next.projectFiles : (state.projectFiles || []),
    };
    persist();
    render();
    const restoredJobId = String(data?.potreeJobId || "").trim();
    if (restoredJobId) {
      potreeJob = { id: restoredJobId, status: "running", progressPct: 0, message: "Restoring job status..." };
      stopPotreePoll();
      potreePollTimer = setInterval(refreshPotreeJob, 1500);
      refreshPotreeJob();
    }
  }

  async function importExternalLasFiles(fileList = []) {
    const files = [...(fileList || [])].filter((f) => /\.(las|laz)$/i.test(String(f?.name || "")));
    if (!files.length) throw new Error("No LAS/LAZ files selected.");
    status = `Uploading ${files.length} LAS file(s) to S3...`;
    render();
    const uploaded = [];
    for (let i = 0; i < files.length; i += 1) {
      const file = files[i];
      const safeName = String(file?.name || "point-cloud.las").replace(/[^a-zA-Z0-9._-]/g, "_");
      status = `Uploading ${i + 1}/${files.length}: ${safeName}`;
      render();

      // Get presigned PUT URL from S3.
      const s3Key = `point-clouds/${Date.now()}-${safeName}`;
      const presignRes = await fetch("/api/s3/presign/upload", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key: s3Key, contentType: "application/octet-stream" }),
      });
      const presignPayload = await presignRes.json().catch(() => ({}));
      if (!presignRes.ok || !presignPayload.url) {
        throw new Error(String(presignPayload?.error || `Failed to get S3 upload URL (${presignRes.status})`));
      }

      // Upload directly to S3 — bypasses the VM disk entirely.
      const uploadRes = await fetch(presignPayload.url, {
        method: "PUT",
        headers: { "Content-Type": "application/octet-stream" },
        body: file,
      });
      if (!uploadRes.ok) throw new Error(`S3 upload failed (${uploadRes.status})`);

      uploaded.push({ name: safeName, sourcePath: presignPayload.key });
    }
    const additions = uploaded.map((item) => ({
      id: makeProjectItemId(),
      name: String(item.name || item.sourcePath.split(/[\\/]/).pop() || "Point Cloud"),
      sourcePath: String(item.sourcePath),
      potreeJobId: "",
      status: "ready",
      crsInspection: null,
    }));
    state.projectFiles = [...(state.projectFiles || []), ...additions];
    if (!state.filePath && additions[0]?.sourcePath) state.filePath = String(additions[0].sourcePath);
    status = `Added ${additions.length} LAS file(s) to project.`;
    persist();
    render();
    Promise.resolve().then(() => inspectProjectFileCrsBatch(additions)).catch(() => {});
    return additions;
  }

  function importExternalLasPaths(paths = []) {
    const normalized = [...(paths || [])]
      .map((p) => String(p || "").trim())
      .filter((p) => !!p && /\.(las|laz)$/i.test(p));
    if (!normalized.length) throw new Error("No LAS/LAZ paths provided.");
    const additions = normalized.map((sourcePath) => ({
      id: makeProjectItemId(),
      name: String(sourcePath.split(/[\\/]/).pop() || "Point Cloud"),
      sourcePath,
      potreeJobId: "",
      status: "ready",
      crsInspection: null,
    }));
    state.projectFiles = [...(state.projectFiles || []), ...additions];
    if (!state.filePath && additions[0]?.sourcePath) state.filePath = String(additions[0].sourcePath);
    status = `Linked ${additions.length} LAS file path(s) to project.`;
    persist();
    render();
    Promise.resolve().then(() => inspectProjectFileCrsBatch(additions)).catch(() => {});
    return additions;
  }

  function setDatasetPath(path = "") {
    state.filePath = String(path || "").trim();
    persist();
    render();
  }

  function setProjectFilesFromDam(keys = []) {
    const normalized = [...(keys || [])]
      .map((p) => String(p || "").trim())
      .filter((p) => !!p && /\.(las|laz)$/i.test(p));
    if (!normalized.length) {
      state.projectFiles = [];
      status = "No LAS files in DAM for this marina.";
      persist();
      render();
      return [];
    }
    const items = normalized.map((sourcePath) => ({
      id: makeProjectItemId(),
      name: String(sourcePath.split(/[\\/]/).pop() || "Point Cloud"),
      sourcePath,
      potreeJobId: "",
      status: "ready",
      selected: true,
      crsInspection: null,
    }));
    state.projectFiles = items;
    if (items[0]?.sourcePath) state.filePath = String(items[0].sourcePath);
    status = `Loaded ${items.length} LAS file(s) from DAM.`;
    persist();
    render();
    Promise.resolve().then(() => inspectProjectFileCrsBatch(items)).catch(() => {});
    return items;
  }

  function loadTilesetOnMap(entry) {
    if (!entry?.tileUrl) return;
    addTileLayerFromDam(entry);
  }

  function setEpsgCode(code) {
    const c = String(code || "").trim();
    if (c) { state.epsgCode = c; persist(); }
  }

  return { mount, destroy, snapshot, restore, setPanelOpen, importExternalLasFiles, importExternalLasPaths, setDatasetPath, setProjectFilesFromDam, setActiveMarina, setEpsgCode, loadTilesetOnMap, removeTilesetFromMap };
}

function mountThreePreview(host, points, libs) {
  const { THREE, OrbitControls } = libs;
  const scene = new THREE.Scene();
  scene.background = new THREE.Color(0x030712);
  const camera = new THREE.PerspectiveCamera(62, 1, 0.1, 1e9);
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
  if ("outputColorSpace" in renderer && THREE?.SRGBColorSpace) {
    renderer.outputColorSpace = THREE.SRGBColorSpace;
  } else if ("outputEncoding" in renderer && THREE?.sRGBEncoding) {
    renderer.outputEncoding = THREE.sRGBEncoding;
  }
  host.innerHTML = "";
  host.appendChild(renderer.domElement);

  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.08;

  const bounds = {
    minX: Infinity, minY: Infinity, minZ: Infinity,
    maxX: -Infinity, maxY: -Infinity, maxZ: -Infinity,
  };
  for (const p of points) {
    const x = Number(p?.x);
    const y = Number(p?.y);
    const z = Number(p?.z);
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
    if (x < bounds.minX) bounds.minX = x;
    if (y < bounds.minY) bounds.minY = y;
    if (z < bounds.minZ) bounds.minZ = z;
    if (x > bounds.maxX) bounds.maxX = x;
    if (y > bounds.maxY) bounds.maxY = y;
    if (z > bounds.maxZ) bounds.maxZ = z;
  }
  const center = {
    x: (bounds.minX + bounds.maxX) / 2,
    y: (bounds.minY + bounds.maxY) / 2,
    z: (bounds.minZ + bounds.maxZ) / 2,
  };
  const spanX = Math.max(1e-6, bounds.maxX - bounds.minX);
  const spanY = Math.max(1e-6, bounds.maxY - bounds.minY);
  const spanZ = Math.max(1e-6, bounds.maxZ - bounds.minZ);
  const maxSpan = Math.max(spanX, spanY, spanZ);
  const zMin = bounds.minZ;
  const zMax = bounds.maxZ;
  const zDen = Math.max(1e-9, zMax - zMin);

  const positions = new Float32Array(points.length * 3);
  const colors = new Float32Array(points.length * 3);
  let idx = 0;
  for (const p of points) {
    const x = Number(p?.x);
    const y = Number(p?.y);
    const z = Number(p?.z);
    if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) continue;
    positions[idx * 3 + 0] = x - center.x;
    positions[idx * 3 + 1] = y - center.y;
    positions[idx * 3 + 2] = z - center.z;
    const t = Math.max(0, Math.min(1, (z - zMin) / zDen));
    const r = 0.15 + (0.85 * t);
    const g = 0.35 + (0.55 * (1 - Math.abs(t - 0.5) * 2));
    const b = 1.0 - (0.55 * t);
    colors[idx * 3 + 0] = r;
    colors[idx * 3 + 1] = g;
    colors[idx * 3 + 2] = b;
    idx += 1;
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  geometry.computeBoundingSphere();
  const material = new THREE.PointsMaterial({
    size: Math.max(maxSpan * 0.0024, 0.6),
    sizeAttenuation: true,
    vertexColors: true,
    opacity: 0.95,
    transparent: true,
  });
  const cloud = new THREE.Points(geometry, material);
  scene.add(cloud);

  const grid = new THREE.GridHelper(maxSpan * 1.2, 16, 0x1e3a8a, 0x0f172a);
  grid.rotation.x = Math.PI / 2;
  grid.position.z = -((spanZ * 0.55) || 0);
  scene.add(grid);

  const lightA = new THREE.DirectionalLight(0xffffff, 0.6);
  lightA.position.set(maxSpan, maxSpan, maxSpan);
  scene.add(lightA);
  scene.add(new THREE.AmbientLight(0x8ec5ff, 0.42));

  function resize() {
    const w = Math.max(120, host.clientWidth || 0);
    const h = Math.max(120, host.clientHeight || 0);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h, false);
  }

  const distance = Math.max(maxSpan * 1.8, 20);
  camera.position.set(distance, -distance * 0.9, distance * 0.65);
  controls.target.set(0, 0, 0);
  controls.update();
  resize();

  let raf = 0;
  let paused = false;
  let destroyed = false;
  const loop = () => {
    if (destroyed || paused) { raf = 0; return; }
    raf = requestAnimationFrame(loop);
    controls.update();
    renderer.render(scene, camera);
  };
  const startLoop = () => {
    if (destroyed || paused || raf) return;
    loop();
  };
  const setPaused = (value) => {
    paused = !!value;
    if (paused && raf) { cancelAnimationFrame(raf); raf = 0; return; }
    startLoop();
  };
  startLoop();

  let ro = null;
  const onWindowResize = () => resize();
  if (typeof ResizeObserver === "function") {
    ro = new ResizeObserver(() => resize());
    ro.observe(host);
  } else {
    window.addEventListener("resize", onWindowResize);
  }

  const destroy = () => {
    destroyed = true;
    try { cancelAnimationFrame(raf); } catch {}
    try { ro?.disconnect?.(); } catch {}
    try { window.removeEventListener("resize", onWindowResize); } catch {}
    try { controls.dispose(); } catch {}
    try { geometry.dispose(); } catch {}
    try { material.dispose(); } catch {}
    try { renderer.dispose(); } catch {}
    try { host.innerHTML = ""; } catch {}
  };

  return { destroy, setPaused };
}
